import { DateTime, Duration } from 'luxon';

import { setAlertsState, clearAlerts } from './slices/alertsSlice';
import { addCallRailCall, addCallRailCallsForDates, deleteCallRailCall } from './slices/callRailCallsSlice';
import { addCampaign, modifyCampaign } from './slices/campaignsSlice';
import { addCase, setCurrentCase, deleteCase } from './slices/casesSlice';
import { setClientsInfo, addClientToClientsInfo, removeClientFromClientsInfo } from './slices/clientsInfoSlice';
import { addDistributionList, updateDistributionList } from './slices/distributionListsSlice';
import { clearDocuments, addDocuments, updateDocument, deleteDocument } from './slices/documentsSlice';
import { addOrUpdateContacts, removeContact } from './slices/emergencyContactsSlice';
import { setEmrName, setEmrClients, setEmrFacilities, setLeadIdToPersonIdMap } from './slices/emrInfoSlice';
import { addFacility } from './slices/facilitiesSlice';
import { setFirmInfo, removeUnaffiliatedContact } from './slices/firmInfoSlice';
import { addFirm, addFirms, updateFirm } from './slices/firmsSlice';
import { setInquiries, updateInquiry, removeInquiry, bulkChangeInquiryOwnerships } from './slices/inquiriesSlice';
import { addInsuranceInfo } from './slices/insuranceInfosSlice';
import { updateClientsFilter, removeClientsFilterKey } from './slices/intendedInfoSlice';
import { addMissive, deleteMissiveWithId } from './slices/missivesSlice';
import {
  addPerson,
  addPersons,
  updatePerson,
  removePerson,
  clearPersons,
  bulkChangeClientOwnerships,
} from './slices/personsSlice';
import { addReferral, updateReferral, deleteReferralWithId } from './slices/referralsSlice';
import { addThinReferrals, deleteThinReferralWithId } from './slices/thinReferralsSlice';
import { addStatusTransitions } from './slices/statusTransitionsSlice';
import { addTask, replaceTask, addTaskableGroup, deleteTaskWithId } from './slices/taskInfoSlice';
import { addTreatmentProgram } from './slices/treatmentProgramsSlice';
import { addVobResolutions } from './slices/vobResolutionsSlice';
import {
  setVisibleFirmsWithContacts,
  removeVisibleFirmContact,
  bulkChangeOrgContactOwnerships,
} from './slices/orgContactsSlice';
import { setOrgTasksForFirm } from './slices/orgTasksSlice';
import { addOutreaches, switchOutreachList } from './slices/outreachListsSlice';
import { setUserSettings, setUserSetting } from './slices/userSettingsSlice';

import {
  addOrUpdateNotesOnCases,
  addOrUpdateNotesOnPersons,
  addOrUpdateNotesOnFirms,
  addOrUpdateNotesOnReferrals,
  addOrUpdateNotesOnTreatmentPrograms,
  addOrUpdateNotesOnFacilities,
  addOrUpdateNotesOnMissives,
  addOrUpdateNotesOnOutreaches,
  addOrUpdateNotesOnNotes,
  addOrUpdateNotesOnInsurance,
  addOrUpdateNotesOnVobResolutions,
  addOrUpdateNotesOnStatusTransitions,
  addOrUpdateNotesOnExtensions,
  addOrUpdateNotesOnDischarges,
  addOrUpdateNotesOnInquiries,
  addOrUpdateNotesOnCallRailCalls,
  removeNoteOnInquiry,
} from './slices/notesSlice';

import {
  addOutreachesFromClient,
  addOutreachesFromEmergencyContact,
  addOutreachesToFirm,
  addOutreachesFromFirm,
  addOutreachesToFirmContact,
  addOutreachesFromFirmContact,
  addOutreachesToDl,
  updateFirmOutreach,
} from './slices/outreachesSlice';

import {
  createStdRequest,
  apiFetch,
  reportTime,
  sortedDropdownItemLabels,
  logWithTime,
  defaultBirthdayKludge,
} from '../utils/utils';

import SocketAccess from '../comm/SocketAccess';

import DA from './ReduxAccess';

// MKM: on 2021-07-13 we had trouble with the linter on the Monitor file
//  so we're commenting it out for now
// import Monitor from './Monitor';

class ReduxLoader {
  store = undefined;
  page = undefined;
  procesWorkListItemsInParallel = true;

  setStore(aStore) {
    this.store = aStore;
  }

  clear() {
    this.page = undefined;
  }

  setPage(page) {
    this.page = page;
  }

  loadFirmInfo(firmId, page, completionCallback) {
    // We're going to piggyback on the fact that this is called on every login
    // We'll sneak in an asynchronous call to load the user's settings and assume that
    //  it will complete in plenty of time for whenever we'll be needing it
    const userId = DA.getLoggedInUserInfo().userId;
    const settingsUri = `/api/user/${userId}/settings`;
    const settingsRequest = createStdRequest(settingsUri);
    const actionOnGoodSettingsResponse = (json) => {
      this.store.dispatch(setUserSettings(json));
    };
    apiFetch(null, settingsRequest, actionOnGoodSettingsResponse);

    // Now, what we really came here for:
    const uri = '/api/firm/' + firmId + '/bigDescrip';
    const request = createStdRequest(uri);
    const actionOnGoodResponse = (json) => {
      reportTime('bigDescrip', b4BigDescripRq);

      const startOfConversion = DateTime.local();

      const persons = json.persons;
      const documents = json.documents || [];
      const alertState = json.alertState;
      const referringFirms = json.knownFirms;
      const dls = json.distributionLists || [];
      const sortedNotes = json.notes;
      const contactIds = json.contactIds || [];
      const campaigns = json.campaigns || [];
      const name = json.name || json.firmName;

      const firmInfo = {
        ...json,
        name,
        contactIds,
        persons: undefined,
        documents: undefined,
        alertState: undefined,
        dls: undefined,
        notes: undefined,
        campaigns: undefined,
      };
      this.store.dispatch(addFirm(firmInfo));
      this.setFirmInfoAndNotifyWebSocket(firmInfo);
      this.store.dispatch(addDocuments(documents));
      const payload = {
        categories: alertState.categories,
        events: alertState.events,
        subscriptions: alertState.subscriptions,
      };
      this.store.dispatch(setAlertsState(payload));

      this.setInquiries(json.inquiries);

      dls.forEach((dl) => this.store.dispatch(addDistributionList(this.convertDlApiStructIntoReduxStruct(dl))));

      campaigns.forEach((campaign) =>
        this.store.dispatch(addCampaign(this.convertCampaignApiStructIntoReduxStruct(campaign)))
      );

      const notesCategoryKeys = Object.keys(sortedNotes);
      notesCategoryKeys.forEach((key) => {
        const notesInCategory = sortedNotes[key];
        if (notesInCategory.length > 0) {
          const capitalizedKey = 'O' + key.substring(1);
          const addFunctionName = 'addOrUpdateNotes' + capitalizedKey;
          const func = window[addFunctionName];
          if (func) {
            this.store.dispatch(func(notesInCategory));
          } else {
            switch (addFunctionName) {
              case 'addOrUpdateNotesOnCases':
                this.addOrUpdateNotesOnCases(notesInCategory);
                break;
              case 'addOrUpdateNotesOnPersons':
                this.addOrUpdateNotesOnPersons(notesInCategory);
                break;
              case 'addOrUpdateNotesOnFirms':
                this.addOrUpdateNotesOnFirms(notesInCategory);
                break;
              case 'addOrUpdateNotesOnReferrals':
                this.addOrUpdateNotesOnReferrals(notesInCategory);
                break;
              case 'addOrUpdateNotesOnTreatmentPrograms':
                this.addOrUpdateNotesOnTreatmentPrograms(notesInCategory);
                break;
              case 'addOrUpdateNotesOnFacilities':
                this.addOrUpdateNotesOnFacilities(notesInCategory);
                break;
              case 'addOrUpdateNotesOnMissives':
                this.addOrUpdateNotesOnMissives(notesInCategory);
                break;
              case 'addOrUpdateNotesOnOutreaches':
                this.addOrUpdateNotesOnOutreaches(notesInCategory);
                break;
              case 'addOrUpdateNotesOnNotes':
                this.addOrUpdateNotesOnNotes(notesInCategory);
                break;
              case 'addOrUpdateNotesOnInsurance':
                this.addOrUpdateNotesOnInsurance(notesInCategory);
                break;
              case 'addOrUpdateNotesOnVobResolutions':
                this.addOrUpdateNotesOnVobResolutions(notesInCategory);
                break;
              case 'addOrUpdateNotesOnStatusTransitions':
                this.addOrUpdateNotesOnStatusTransitions(notesInCategory);
                break;
              case 'addOrUpdateNotesOnExtensions':
                this.addOrUpdateNotesOnExtensions(notesInCategory);
                break;
              case 'addOrUpdateNotesOnDischarges':
                this.addOrUpdateNotesOnDischarges(notesInCategory);
                break;
              case 'addOrUpdateNotesOnInquiries':
                this.addOrUpdateNotesOnInquiries(notesInCategory);
                break;
              default:
                console.log('Failed to handle case where note-adding-func = ' + addFunctionName);
                break;
            }
          }
        }
      });

      // Try setting up clientsInfo store from json
      const clientsIdsAndNames = json.clientsIdsAndNames;
      const clientIds = clientsIdsAndNames.map((pair) => pair.id);
      const clientsInfo = {
        firmId: firmId,
        nClients: clientsIdsAndNames.length,
        clientIds: clientIds,
      };
      this.store.dispatch(setClientsInfo(clientsInfo));

      // reportTime('clientIds', beforeClientIds);

      /*
       * Here is what is done with the results of /api/firm/<firmId>/visibleFirm
       * We want to accomplish the same thing here so we don't need to make that call.
       * Here's the code for that case, Note that each 'firm' here is a FirmAsReferralSourceApiStruct.
            const referringFirms = json;
            referringFirms.forEach(firm => {
                this.store.dispatch(addVisibleFirmWithContacts({ visibleFirmId: firm.firmId, contactIdsArray: firm.contactIds }));
                this.store.dispatch(addFirm(firm));
            });

        We have to do the same thing, but with firms represented by ReferralSource objects
        
        We Have: [ReferralSource]
          public int Id
          public string Name
          public string FirmType
          public string Description
          public string OfficePhone
          public ReferralSourceAddress Address
          public IList<PersonIdAndNamePair> Contacts
          public List<WebLinkApiStruct> WebLinks
        We Want: [FirmAsReferralSourceApiStruct]
          public int FirmId [get from Id]
          public string Name
          public string Description
          public string OfficePhone
          public ReferralSourceAddress Address
          public string FirmType
          public List<int> ContactIds [get from mapping Contacts]
          public List<WebLinkApiStruct> WebLinks
       */

      const augmentedFirms = referringFirms.map((referralSource) => {
        const contactIds = referralSource.contacts.map((contactIdAndName) => contactIdAndName.id);
        return { ...referralSource, firmId: referralSource.id, contactIds };
      });
      this.store.dispatch(addFirms(augmentedFirms));

      const reduxPersons = persons.map((person) => this.convertPersonApiStructIntoReduxStruct(person));
      this.store.dispatch(addPersons(reduxPersons));

      // Done aping the action on response to /visibleFirm request
      // Now we'll ape the action on response to /visibleFirmContact request
      // That returns an array of FirmAndContactsApiStruct objects
      // So we'll produce just such an array from our response to the /bigDescrip call

      const arrayOfFirmAndContactsApiStructObjects = referringFirms.map((visibleFirm) =>
        this.createFirmAndContactsApiStructObject(visibleFirm)
      );

      // And now we'll process that array just the way we would for /visibleFirmContact

      const firmAndContactIdsArray = this.extractFirmAndContactIdsArrayForEach(arrayOfFirmAndContactsApiStructObjects);

      this.store.dispatch(setVisibleFirmsWithContacts(firmAndContactIdsArray));

      this.initProspectLeadTemperatureInclusionFilterState();

      this.store.dispatch(setEmrName(json.syncedEmr));

      if (!!json.syncedEmr) {
        const continuation2 = () => this.fetchAndStoreEmrFacilitiesList(page);
        const continuation1 = () => this.fetchAndStoreEmrClientsList(page, continuation2);
        this.fetchAndStoreExternalMappingTable(page, continuation1);
      }
      reportTime('conversion', startOfConversion);

      if (completionCallback) {
        completionCallback();
      }
    };

    const b4BigDescripRq = DateTime.local();
    apiFetch(page, request, actionOnGoodResponse);
  }

  fetchAndStoreExternalMappingTable(page, continuation) {
    const firmId = DA.getFirmId();
    const uri = `/api/firm/${firmId}/leadIdToPersonIdMap`;
    const request = createStdRequest(uri);
    const actionOnGoodResponse = (json) => {
      this.store.dispatch(setLeadIdToPersonIdMap(json));
      continuation();
    };
    apiFetch(page, request, actionOnGoodResponse);
  }

  fetchAndStoreEmrClientsList(page, continuation) {
    const firmId = DA.getFirmId();
    const prospectsUri = `/api/emrcall/firm/${firmId}/prospects`;
    const prospectsReq = createStdRequest(prospectsUri);

    const actionOnGoodProspectsResponse = (prospectsJson) => {
      const emrProspects = prospectsJson;

      const clientsUri = `/api/emrcall/firm/${firmId}/clients`;
      const clientsReq = createStdRequest(clientsUri);

      const actionOnGoodClientsResponse = (clientsJson) => {
        const emrPureClients = clientsJson;

        const emrClients = [...emrProspects, ...emrPureClients];

        if (emrClients.length > 0) {
          const emrClientsWithPersonIds = emrClients.map((emrC) => this.addPersonIdToEmrClient(emrC));

          // MKM: Remove diagnostic logging when we've fixed the 'disappearing facilities' issue
          const nClients = emrClientsWithPersonIds.length;
          console.log(`calling setEmrClients() with a list of ${nClients} clients`);
          if (nClients > 0) {
            const firstClient = emrClientsWithPersonIds[0];
            console.log('example (should have reasonable personId):');
            console.log(firstClient);
          }

          this.store.dispatch(setEmrClients(emrClientsWithPersonIds));
        }
        continuation();
      };
      apiFetch(page, clientsReq, actionOnGoodClientsResponse);
    };
    apiFetch(page, prospectsReq, actionOnGoodProspectsResponse);
  }

  fetchAndStoreEmrFacilitiesList(page) {
    const firmId = DA.getFirmId();
    const uri = `/api/emrcall/firm/${firmId}/facilities`;
    const req = createStdRequest(uri);

    const actionOnGoodResponse = (json) => {
      const emrFacilities = json;
      if (emrFacilities.length > 0) {
        this.store.dispatch(setEmrFacilities(emrFacilities));
      }
    };

    apiFetch(page, req, actionOnGoodResponse);
  }

  addPersonIdToEmrClient(emrClient) {
    /*
    const personsObj = this.store.getState().persons;
    const personsArray = Object.values(personsObj);
    const matchingPerson = personsArray.find((per) => per.externalPersonId === emrClient.id.toString());
    const personId = matchingPerson ? matchingPerson.personId : 0;
    */
    const emrInfo = this.store.getState().emrInfo;
    const leadIdToPersonIdMap = emrInfo.leadIdToPersonIdMap;
    const leadId = emrClient.id.toString();
    const personId = leadIdToPersonIdMap[leadId] || 0;
    return { ...emrClient, personId };
  }

  bestPhoneNumber(clientObject) {
    const phones = clientObject.phoneNumbers;
    if (!phones || phones.length === 0) {
      return '';
    }
    const firstMobile = phones.find((po) => po.roles.indexOf('Mobile') >= 0);
    return firstMobile ? firstMobile.number : phones[0].number;
  }

  removePunctuationFromPhoneNumber(number) {
    if (!number || number.length === 0) {
      return '';
    } else {
      let newNumber = number.match(/\d/g);
      newNumber = newNumber.join('');
      return newNumber;
    }
  }

  setFirmInfoAndNotifyWebSocket(firmInfo) {
    this.store.dispatch(setFirmInfo(firmInfo));
    SocketAccess.setWebSocketFirmId();
  }

  setInquiries(inquiries) {
    const reduxStructs = this.convertInquiriesToReduxFormat(inquiries);
    this.store.dispatch(setInquiries(reduxStructs));
  }

  updateInquiry(inquiry) {
    const reduxStruct = this.convertInquiryToReduxFormat(inquiry);
    this.store.dispatch(updateInquiry(reduxStruct));
  }

  convertInquiriesToReduxFormat(inquiries) {
    return inquiries.map((i) => this.convertInquiryToReduxFormat(i));
  }

  convertInquiryToReduxFormat(inquiry) {
    // const dateIso = inquiry.date.toISO();
    // return { ...inquiry, date: dateIso };
    if (!inquiry.referralSourceCategory) {
      return { ...inquiry, referralSourceCategory: 'Unknown' };
    } else {
      return inquiry;
    }
  }

  addCampaign(campaignApiStruct) {
    const reduxStruct = this.convertCampaignToReduxFormat(campaignApiStruct);
    this.store.dispatch(addCampaign(reduxStruct));
  }

  modifyCampaign(campaignApiStruct) {
    const reduxStruct = this.convertCampaignToReduxFormat(campaignApiStruct);
    this.store.dispatch(modifyCampaign(reduxStruct));
  }

  convertCampaignToReduxFormat(campaignApiStruct) {
    return campaignApiStruct;
  }

  initProspectLeadTemperatureInclusionFilterState() {
    const reduxState = this.store.getState();
    const temperatureNames = new Set();

    // first collect all the values from the "Prospect Status" dropdown
    const temperatureOptions = sortedDropdownItemLabels('Prospect Status');
    temperatureOptions.forEach((option) => {
      temperatureNames.add(option);
    });

    // now add all the values that exist in any clients
    const clientIds = reduxState.clientsInfo.clientIds;
    clientIds.forEach((clientId) => {
      const client = DA.getPersonById(clientId);
      if (client.leadTemperature) {
        temperatureNames.add(client.leadTemperature);
      }
    });

    let clientsFilter = reduxState.intendedInfo.clientsPageFilter;

    const oldStateVariableNames = new Set();
    Object.keys(clientsFilter).forEach((stateVariableName) => oldStateVariableNames.add(stateVariableName));

    temperatureNames.forEach((name) => {
      const stateVariableName = 'show' + name;
      oldStateVariableNames.delete(stateVariableName);
      if (!clientsFilter.hasOwnProperty(stateVariableName)) {
        this.store.dispatch(updateClientsFilter({ filterItemName: stateVariableName, value: true }));
        clientsFilter = this.store.getState().intendedInfo.clientsPageFilter;
      }
    });

    if (oldStateVariableNames.size > 0) {
      oldStateVariableNames.forEach((stateVariableName) =>
        this.store.dispatch(removeClientsFilterKey(stateVariableName))
      );
    }
  }

  createFirmAndContactsApiStructObject(visibleFirmStruct) {
    /* visibleFirmStruct is a ReferralSource object, like:
      public int Id
      public string Name
      public string FirmType
      public string Description
      public string OfficePhone
      public ReferralSourceAddress Address
      public IList<PersonIdAndNamePair> Contacts
      public List<WebLinkApiStruct> WebLinks
    We want to produce a FirmAndContactsApiStruct object, like:
      public int FirmId
      public List<FirmContactApiStruct> Contacts
    Shouldn't be too hard
     */

    // rename it for type clarity
    const referralSource = visibleFirmStruct;
    const contactIds = referralSource.contacts.map((contactIdAndName) => contactIdAndName.id);
    const contacts = contactIds.map((contactId) => DA.getPersonById(contactId));
    return { firmId: referralSource.id, contacts: contacts };
  }

  quasiLoadStateForNoFirm() {
    this.store.dispatch(clearPersons());
    this.store.dispatch(clearDocuments());
    this.store.dispatch(clearAlerts());
  }

  loadCasePageInfoForClient(page, clientId, completionCallback) {
    this.setPage(page);
    const workList = [{ objType: 'latestCaseForClient', id: clientId }];
    this.processWorkList(workList, completionCallback);
  }

  loadInfoForReferrerContact(page, viewingFirmId, visibleFirmId, contactId, completionCallback) {
    this.setPage(page);
    const triple = { viewingFirmId, visibleFirmId, contactId };
    const remainingWork = [{ objType: 'infoForReferrerContact', triple }];
    this.processWorkList(remainingWork, completionCallback);
  }

  loadInfoForEmergencyContact(page, viewingFirmId, contactId, completionCallback) {
    this.setPage(page);
    const remainingWork = [{ objType: 'infoForEmergencyContact', id: contactId }];
    this.processWorkList(remainingWork, completionCallback);
  }

  loadInfoForReferrerOrg(page, visibleFirmId, completionCallback) {
    this.setPage(page);
    const remainingWork = [{ objType: 'infoForReferrerFirm', id: visibleFirmId }];
    this.processWorkList(remainingWork, completionCallback);
  }

  level = 0;

  trace(str) {
    // console.log(str);
  }

  processWorkList(workList, completionCallback) {
    if (this.procesWorkListItemsInParallel) {
      this.level += 1;
      this.trace(`starting a list of length ${workList.length} at level ${this.level}`);
      workList.forEach((item) => this.trace(`    ${item.objType}`));

      const promises = workList.map((item) => this.createPromiseForWorkItem(item));
      const aggregatedPromise = Promise.allSettled(promises);
      aggregatedPromise.then(() => {
        this.level -= 1;
        this.trace(`finished ${workList.length}-length list at level ${this.level + 1}`);
        if (completionCallback) {
          this.trace(`calling completionCallback at level ${this.level + 1}`);
          completionCallback();
        } else {
          this.trace(`no completionCallback to call at level ${this.level + 1}`);
        }
      });
    } else {
      /*
       * Processing sibling nodes in parallel seems to work so far.
       * If we discover conflicts, we can try implementing a completely serial version
       */
    }
  }

  createPromiseForWorkItem(workItem) {
    const functionToExecute = (resolve, reject) => {
      const itemCompletionFunc = () => {
        this.trace(`inside completionFunc for item ${workItem.objType} at level ${this.level}`);
        this.trace(`  calling resolve()`);
        resolve(workItem);
      };

      this.processWorkItem(workItem, itemCompletionFunc);
    };
    return new Promise(functionToExecute);
  }

  standardSerialSuccessFunc(tail, completionCallback) {
    // The item succeeded, so we'll move on to the tail in a recursive call
    if (tail.length > 0) {
      const nextItem = tail[0];
      const remainingTail = tail.slice(1);
      this.executeHeadThenTail(nextItem, remainingTail, completionCallback);
    } else {
      this.level -= 1;
      if (completionCallback) {
        this.trace(`executing completionCallback (end of workList) at level ${this.level}`);
        completionCallback();
      }
    }
  }

  standardFailureFunc(workItem) {
    this.trace(`Failed on workItem with objType: ${workItem.objType} and id ${workItem.id}`);
  }

  executeHeadThenTail(workItem, tail, completionCallback) {
    const promise = this.createPromiseToExecute(workItem); // this should start it running

    const successFunc = () => this.standardSerialSuccessFunc(tail, completionCallback);
    const faiureFunc = () => this.standardFailureFunc(workItem);

    promise.then(successFunc, faiureFunc);
  }

  createPromiseToExecute(workItem) {
    const functionToExecute = (resolve, reject) => {
      const workItemCompletionFunc = () => {
        resolve(workItem);
      };

      this.processWorkItem(workItem, workItemCompletionFunc);
    };
    return new Promise(functionToExecute);
  }

  processWorkItem(workItem, completionFunc) {
    let clientId = 0;
    let personId = 0;
    let contactId = 0;
    let viewingFirmId = 0;
    let visibleFirmId = 0;
    let referralId = 0;
    this.trace(`executing workItem of type ${workItem.objType} at level ${this.level}`);
    switch (workItem.objType) {
      case 'latestCaseForClient':
        // Monitor.setToCapture(true, 'case');
        clientId = workItem.id;
        this.loadBigDescripForLatestCaseForClient(clientId, completionFunc);
        break;
      case 'infoForReferrerContact':
        contactId = workItem.triple.contactId;
        if (contactId !== 0) {
          const childWorkList = [
            { objType: 'contactAtVisibleFirm', triple: workItem.triple },
            { objType: 'outreachHistory', id: contactId },
            { objType: 'tasksForSubject', id: contactId },
            { objType: 'thinReferralsFromContact', triple: workItem.triple },
            { objType: 'notesOnPerson', id: contactId },
          ];
          this.processWorkList(childWorkList, completionFunc);
        } else {
          completionFunc();
        }
        break;
      case 'thinReferralsFromContact':
        contactId = workItem.triple.contactId;
        if (contactId !== 0 && !this.alreadyLoadedThinReferralsFromContact(contactId)) {
          this.loadThinReferrals(workItem.triple, completionFunc);
        } else {
          completionFunc();
        }
        break;
      case 'infoForEmergencyContact':
        contactId = workItem.id;
        if (contactId !== 0) {
          const childWorkList = [
            { objType: 'emergencyContactAtViewingFirm', id: contactId },
            { objType: 'outreachHistory', id: contactId },
            { objType: 'tasksForSubject', id: contactId },
            { objType: 'notesOnPerson', id: contactId },
          ];
          this.processWorkList(childWorkList, completionFunc);
        } else {
          completionFunc();
        }
        break;
      case 'infoForReferrerFirm':
        visibleFirmId = workItem.id;
        if (visibleFirmId !== 0) {
          const childWorkList = [
            { objType: 'firmAsReferrerOrg', id: visibleFirmId },
            { objType: 'notesAboutFirm', id: visibleFirmId },
            { objType: 'outreachHistoryWithFirm', id: visibleFirmId },
            { objType: 'tasksForFirm', id: visibleFirmId },
          ];
          this.processWorkList(childWorkList, completionFunc);
        } else {
          completionFunc();
        }
        break;
      case 'contactAtVisibleFirm':
        contactId = workItem.triple.contactId;
        viewingFirmId = workItem.triple.viewingFirmId;
        visibleFirmId = workItem.triple.visibleFirmId;
        if (contactId !== 0 && !this.alreadyLoadedContactAtVisibleFirm(contactId)) {
          if (visibleFirmId === 0) {
            if (!this.alreadyLoadedPerson(contactId)) {
              this.trace(`  visibleFirmid == 0 and person ${contactId} not yet loaded, so calling loadPerson()`);
              this.loadPerson(contactId, completionFunc);
            } else {
              completionFunc();
            }
          } else {
            this.trace(`  visibleFirmid nonZero so calling loadContactAtVisibleFirm()`);
            this.loadContactAtVisibleFirm(viewingFirmId, visibleFirmId, contactId, completionFunc);
          }
        } else {
          this.trace(`  contactId ${contactId} 0 or already loaded, calling completionFunc for item`);
          completionFunc();
        }
        break;
      case 'emergencyContactAtViewingFirm':
        contactId = workItem.id;
        if (contactId !== 0 && !this.alreadyLoadedEmergencyContact(contactId) && !this.alreadyLoadedPerson(contactId)) {
          this.loadPerson(contactId, completionFunc);
        } else {
          completionFunc();
        }
        break;
      case 'person':
        personId = workItem.id;
        if (personId !== 0 && !this.alreadyLoadedPerson(personId)) {
          this.loadPerson(personId, completionFunc);
        } else {
          completionFunc();
        }
        break;
      case 'client':
        clientId = workItem.id;
        if (clientId !== 0) {
          const childWorkList = [
            { objType: 'person', id: clientId },
            { objType: 'tasksForSubject', id: clientId },
            { objType: 'emergencyContacts', id: clientId },
            { objType: 'notesOnPerson', id: clientId },
            { objType: 'statusHistory', id: clientId },
          ];
          this.processWorkList(childWorkList, completionFunc);
        } else {
          completionFunc();
        }
        break;
      case 'case':
        const caseId = workItem.id;
        if (caseId !== 0 && !this.alreadyLoadedCase(caseId)) {
          this.loadCase(caseId, completionFunc);
        } else {
          completionFunc();
        }
        break;
      case 'notesOnPerson':
        clientId = workItem.id;
        if (clientId !== 0 && !this.alreadyLoadedNotesOnPerson(clientId)) {
          this.loadNotesOnPerson(clientId, completionFunc);
        } else {
          completionFunc();
        }
        break;
      case 'notesAboutFirm':
        visibleFirmId = workItem.id;
        if (visibleFirmId !== 0 && !this.alreadyLoadedNotesAboutFirm(visibleFirmId)) {
          this.loadNotesAboutFirm(visibleFirmId, completionFunc);
        } else {
          completionFunc();
        }
        break;
      case 'notesAboutReferral':
        referralId = workItem.id;
        if (referralId !== 0 && !this.alreadyLoadedNotesAboutReferral(referralId)) {
          this.loadNotesAboutReferral(referralId, completionFunc);
        } else {
          completionFunc();
        }
        break;
      case 'firm':
        const firmId = workItem.id;
        if (firmId !== 0 && !this.alreadyLoadedFirm(firmId)) {
          this.loadFirm(firmId, completionFunc);
        } else {
          completionFunc();
        }
        break;
      case 'firmAsReferrerOrg':
        const firmAsReferralOrgId = workItem.id;
        if (firmAsReferralOrgId !== 0 && !this.alreadyLoadedFirmAsReferrerOrg(firmAsReferralOrgId)) {
          this.loadFirmAsReferrerOrg(firmAsReferralOrgId, completionFunc);
        } else {
          completionFunc();
        }
        break;
      case 'referral':
        referralId = workItem.id;
        if (referralId !== 0 && !this.alreadyLoadedReferral(referralId)) {
          this.loadReferral(referralId, completionFunc);
        } else {
          completionFunc();
        }
        break;
      case 'emergencyContacts':
        clientId = workItem.id;
        if (clientId !== 0 && !this.alreadyLoadedContacts(clientId)) {
          this.loadEmergencyContactsOfClient(clientId, completionFunc);
        } else {
          completionFunc();
        }
        break;
      case 'tasksForSubject':
        const subjectId = workItem.id;
        if (subjectId !== 0) {
          this.loadTasksForSubject(subjectId, completionFunc);
        } else {
          completionFunc();
        }
        break;
      case 'tasksForFirm':
        visibleFirmId = workItem.id;
        if (visibleFirmId !== 0 && !this.alreadyLoadedTasksForFirm(visibleFirmId)) {
          this.loadTasksForFirm(visibleFirmId, completionFunc);
        } else {
          completionFunc();
        }
        break;
      case 'assignableGroup':
        const groupId = workItem.id;
        if (groupId !== 0 && !this.alreadyLoadedAssignableGroup(groupId)) {
          this.loadAssignableGroup(groupId, completionFunc);
        } else {
          completionFunc();
        }
        break;
      case 'treatmentProgram':
        const treatmentProgramId = workItem.id;
        if (treatmentProgramId !== 0 && !this.alreadyLoadedTreatmentProgram(treatmentProgramId)) {
          this.loadTreatmentProgram(treatmentProgramId, completionFunc);
        } else {
          completionFunc();
        }
        break;
      case 'facility':
        const facilityId = workItem.id;
        if (facilityId !== 0 && !this.alreadyLoadedFacility(facilityId)) {
          this.loadFacility(facilityId, completionFunc);
        } else {
          completionFunc();
        }
        break;
      case 'outreachHistory':
        personId = workItem.id;
        if (personId !== 0 && !this.alreadyLoadedOutreachHistoryForPerson(personId)) {
          this.trace(`  history not loaded, so calling loadOutreachHistoryForPerson(${personId})`);
          this.loadOutreachHistoryForPerson(this.page, personId, completionFunc);
        } else {
          this.trace(`  person ${personId} 0 or already loaded, so calling completionFunc for item`);
          completionFunc();
        }
        break;
      case 'outreachHistoryWithFirm':
        visibleFirmId = workItem.id;
        if (visibleFirmId !== 0 && !this.alreadyLoadedOutreachHistoryWithFirm(visibleFirmId)) {
          this.loadOutreachHistoryWithFirm(this.page, visibleFirmId, completionFunc);
        } else {
          completionFunc();
        }
        break;
      case 'statusHistory':
        clientId = workItem.id;
        if (clientId !== 0 && !this.alreadyLoadedStatusHistoryForClient(clientId)) {
          this.loadStatusHistoryForClient(clientId, completionFunc);
        } else {
          completionFunc();
        }
        break;
      case 'dl':
        const dlId = workItem.id;
        if (dlId !== 0 && !this.alreadyLoadedDl(dlId)) {
          this.loadDl(dlId, completionFunc);
        } else {
          completionFunc();
        }
        break;
      case 'missive':
        const missiveId = workItem.id;
        if (missiveId !== 0 && !this.alreadyLoadedMissive(missiveId)) {
          this.loadMissive(missiveId, completionFunc);
        } else {
          completionFunc();
        }
        break;
      case 'callRailCall':
        const callRailCallId = workItem.id;
        if (callRailCallId !== 0 && !this.alreadyLoadedCallRailCall(callRailCallId)) {
          this.loadCallRailCall(callRailCallId, completionFunc);
        } else {
          completionFunc();
        }
        break;
      case 'campaign':
        const campaignId = workItem.id;
        if (campaignId !== 0 && !this.alreadyLoadedCampaign(campaignId)) {
          this.loadCampaign(campaignId, completionFunc);
        } else {
          completionFunc();
        }
        break;
      case 'insuranceInfo':
        const insuranceInfoId = workItem.id;
        if (insuranceInfoId !== 0 && !this.alreadyLoadedInsuranceInfo(insuranceInfoId)) {
          clientId = workItem.id2;
          this.loadInsuranceInfo(clientId, insuranceInfoId, completionFunc);
        } else {
          completionFunc();
        }
        break;
      case 'vobs':
        clientId = workItem.id;
        const insuranceInfoId2 = workItem.id2; // weirdly named to avoid scope problems
        this.loadVobs(clientId, insuranceInfoId2, completionFunc);
        break;
      default:
        console.log('processWorkList() sees unknown objType: ' + workItem.objType);
        break;
    }
  }

  loadThinReferrals(triple, completionFunc) {
    const contactId = triple.contactId;
    const viewingFirmId = triple.viewingFirmId;
    const visibleFirmId = triple.visibleFirmId;
    const uri =
      visibleFirmId > 0
        ? `/api/firm/${viewingFirmId}/visibleFirm/${visibleFirmId}/contact/${contactId}/thinReferral`
        : `/api/firm/${viewingFirmId}/contact/${contactId}/thinReferral`;
    const request = createStdRequest(uri);
    const goodResponseAction = (responseJson) => {
      // The hope is that these "thin" referral records are enough to support the required functionality.
      // So we'll just store them in Redux
      /*
      const childWorkList = responseJson.map(refId => {
        return { objType: 'referral', id: refId };
      });
      this.processWorkList(childWorkList, completionFunc);
      */
      this.store.dispatch(addThinReferrals(responseJson));
      completionFunc();
    };
    apiFetch(this.page, request, goodResponseAction);
  }

  loadPerson(personId, completionFunc) {
    const uri = '/api/person/' + personId;
    const request = createStdRequest(uri);
    const goodResponseAction = (responseJson) => {
      const personApiStruct = responseJson;
      // Monitor.recordReceivedStruct('person', personId, personApiStruct);
      const reduxStruct = this.convertPersonApiStructIntoReduxStruct(personApiStruct);
      this.store.dispatch(addPerson(reduxStruct));
      completionFunc();
    };
    apiFetch(this.page, request, goodResponseAction);
  }

  addClientToClientsInfo(clientId) {
    this.store.dispatch(addClientToClientsInfo(clientId));
  }

  loadOrRefreshPerson(personId, continuation) {
    const merelyUpdate = !!DA.getPersonById(personId);

    const uri = '/api/person/' + personId;
    const request = createStdRequest(uri);
    const goodResponseAction = (responseJson) => {
      const personApiStruct = responseJson;
      // Monitor.recordReceivedStruct('person', personId, personApiStruct);
      const reduxStruct = this.convertPersonApiStructIntoReduxStruct(personApiStruct);
      if (merelyUpdate) {
        this.store.dispatch(updatePerson(reduxStruct));
      } else {
        console.log('Dispatching addPerson()');
        this.store.dispatch(addPerson(reduxStruct));
      }
      continuation();
    };
    apiFetch(null, request, goodResponseAction);
  }

  loadOrRefreshClient(personId, continuation) {
    const merelyUpdate = !!DA.getPersonById(personId);

    const uri = '/api/client/' + personId;
    const request = createStdRequest(uri);
    const goodResponseAction = (responseJson) => {
      const personApiStruct = responseJson;
      // Monitor.recordReceivedStruct('person', personId, personApiStruct);
      const reduxStruct = this.convertPersonApiStructIntoReduxStruct(personApiStruct);
      if (merelyUpdate) {
        this.store.dispatch(updatePerson(reduxStruct));
      } else {
        console.log('Dispatching addPerson()');
        this.store.dispatch(addPerson(reduxStruct));
      }
      continuation();
    };
    apiFetch(null, request, goodResponseAction);
  }

  convertPersonApiStructIntoReduxStruct(personApiStruct) {
    /* output should look like :
                {
                        personId: 42,
                        preferredName: "John",
                        lastName: "Fogerty",
                        sex: "M",
                        phoneNumbers: [{roles: "Mobile,Home", number: "415-365-2617"}],
                        email: "jfogerty@gmail.com",
                        addresses: [],
                        status: "Blah",
                        inactive: bool,
                        temperature: 'Hot'
                }
        */
    /* input looks like:
                public int PersonId { get; set; }
                public string Email { get; set; }
                public string FirstName { get; set; }
                public string MiddleNames { get; set; }
                public string LastName { get; set; }
                public string PreferredName { get; set; }
                public string Sex { get; set; }
                public long BirthDate { get; set; }
                public OfficialIdApiStruct OfficialId { get; set; }
                public List<PhoneApiStruct> PhoneNumbers { get; set; } = new List<PhoneApiStruct>();
                public List<AddressApiStruct> Addresses { get; set; } = new List<AddressApiStruct>();
                public string Status { get; set; }
                public bool Inactive { get; set; }
                public string Temperature { get; set; }
        */
    if (Object.keys(personApiStruct).indexOf('officialId') >= 0) {
      return this.createReduxStructForFetchedPersonOrContactApiStruct(personApiStruct);
    } else {
      personApiStruct.preferredFullName = personApiStruct.preferredName + ' ' + (personApiStruct.lastName || '');
      return personApiStruct;
    }
  }

  loadContactAtVisibleFirm(viewingFirmId, visibleFirmId, contactId, completionFunc) {
    const uri = `/api/firm/${viewingFirmId}/visibleFirm/${visibleFirmId}/contact/${contactId}`;
    const request = createStdRequest(uri);
    const goodResponseAction = (responseJson) => {
      const contactApiStruct = responseJson;
      const reduxStruct = this.convertContactAtFirmApiStructIntoReduxStruct(contactApiStruct);
      this.store.dispatch(addPerson(reduxStruct));
      completionFunc();
    };
    apiFetch(this.page, request, goodResponseAction);
  }

  convertContactAtFirmApiStructIntoReduxStruct(contactApiStruct) {
    /* output should look like :
                {
                        personId: 42,
                        preferredName: "John",
                        lastName: "Fogerty",
                        preferredFullName: "John Fogerty",
                        sex: "M",
                        phoneNumbers: [{roles: "Mobile,Home", number: "415-365-2617"}],
                        email: "jfogerty@gmail.com",
                        addresses: [],
                        firmId: 34,
                        viewingFirmId: 38,
                        jobTitle: "CEO",
                        description: "Some kind of description",
                        temperature: "Hot",
                }
        */
    /* input looks like:
                public int PersonId { get; set; }
                public string Email { get; set; }
                public string FirstName { get; set; }
                public string MiddleNames { get; set; }
                public string LastName { get; set; }
                public string PreferredName { get; set; }
                public string Sex { get; set; }
                public long BirthDate { get; set; }
                public OfficialIdApiStruct OfficialId { get; set; }
                public List<PhoneApiStruct> PhoneNumbers { get; set; } = new List<PhoneApiStruct>();
                public List<AddressApiStruct> Addresses { get; set; } = new List<AddressApiStruct>();

                public int FirmId { get; set; }
                public int ViewingFirmId { get; set; }
                public string JobTitle { get; set; }
                public string Description { get; set; }
                public string Temperature { get; set; }
        */
    if (Object.keys(contactApiStruct).indexOf('officialId') >= 0) {
      return this.createReduxStructForFetchedPersonOrContactApiStruct(contactApiStruct);
    } else {
      contactApiStruct.preferredFullName = contactApiStruct.preferredName + ' ' + (contactApiStruct.lastName || '');
      return contactApiStruct;
    }
  }

  loadCase(caseId, completionFunc) {
    const uri = '/api/case/' + caseId;
    const request = createStdRequest(uri);
    const goodResponseAction = (responseJson) => {
      const caseApiStruct = responseJson;
      // Monitor.recordReceivedStruct('case', caseId, caseApiStruct);
      const reduxStruct = this.convertCaseApiStructIntoReduxStruct(caseApiStruct);
      this.store.dispatch(addCase(reduxStruct));
      this.store.dispatch(setCurrentCase(reduxStruct));
      const someNewWork = [
        { objType: 'client', id: reduxStruct.clientId },
        { objType: 'person', id: reduxStruct.admissionOwnerId },
        { objType: 'person', id: reduxStruct.primaryClinicianId },
        { objType: 'outreachHistory', id: reduxStruct.clientId },
        { objType: 'referral', id: reduxStruct.referralId },
        { objType: 'insuranceInfo', id: reduxStruct.insuranceInfoId, id2: reduxStruct.clientId },
      ];
      const usersWork = DA.getAllFirmUsers().map((user) => {
        return { objType: 'person', id: user.personId };
      });
      const treatmentWork = caseApiStruct.treatmentProgramIds.map((id) => {
        return { objType: 'treatmentProgram', id: id };
      });
      const childWork = [...someNewWork, ...usersWork, ...treatmentWork];
      this.processWorkList(childWork, completionFunc);
    };
    apiFetch(this.page, request, goodResponseAction);
  }

  convertCaseApiStructIntoReduxStruct(caseApiStruct) {
    /* output should be of form:
                {
                    caseId: caseId,
                    clientId: 42,
                    firmId: 2,
                    creationDate: isoString,
                    referralId: 12,
                    firstContactDate: isoString,
                    salesStage: "Initial Contact",
                    leadTemperature: "Hot",
                    insuranceInfoId: 55,
                    treatmentProgramIds: [16],
                    admissionOwnerId: 16,
                    primaryClinicianId: 22,
                    admissionDate: isoString,
                    dischargeDate: isoString
                };
        */
    /* input is of the form:
                public int CaseId { get; set; }
                public string CreationDate { get; set; }
                public int ClientId { get; set; }
                public List<string> SubstanceNameStarts { get; set; } = new List<string>();
                public int ReferralId { get; set; }
                public string AdmissionDate { get; set; }
                public int AdmissionOwnerId { get; set; }
                public int PrimaryClinicianId { get; set; }
                public int FacilityId { get; set; }
                public int InsuranceInfoId { get; set; }
                public List<int> TreatmentProgramIds { get; set; } = new List<int>();
                public string SalesStage { get; set; }
                public string LeadTemperature { get; set; }
                public string DischargeDate { get; set; }
                public List<ExtensionOfStayApiStruct> Extensions { get; set; } = new List<ExtensionOfStayApiStruct>();
        */
    const givenAdmissionDate = caseApiStruct.admissionDate;
    const givenDischargeDate = caseApiStruct.dischargeDate;
    const admissionDateToStore =
      !givenAdmissionDate || givenAdmissionDate.startsWith('000') ? defaultBirthdayKludge.toISO() : givenAdmissionDate;
    const dischargeDateToStore =
      !givenDischargeDate || givenDischargeDate.startsWith('000') ? defaultBirthdayKludge.toISO() : givenDischargeDate;
    return {
      ...caseApiStruct,
      firstContactDate: caseApiStruct.creationDate,
      admissionDate: admissionDateToStore,
      dischargeDate: dischargeDateToStore,
    };
  }

  loadBigDescripForLatestCaseForClient(clientId, completionFunc) {
    const firmId = DA.getFirmId();
    const uri = `/api/case/firm/${firmId}/client/${clientId}/latest`;
    const request = createStdRequest(uri);
    const goodResponseAction = (responseJson) => {
      reportTime('loadBigCaseDescrip', b4Fetch);
      const b4Processing = DateTime.local();
      this.processBigCaseDescrip(responseJson);
      reportTime('processBigCaseDescrip', b4Processing);
      completionFunc();
    };
    const b4Fetch = DateTime.local();
    apiFetch(this.page, request, goodResponseAction);
  }

  processBigCaseDescrip(bigCaseDescrip) {
    const caseApiStruct = bigCaseDescrip.case;
    const clientId = caseApiStruct.clientId;
    const reduxCaseStruct = this.convertCaseApiStructIntoReduxStruct(caseApiStruct);
    this.store.dispatch(addCase(reduxCaseStruct));
    this.store.dispatch(setCurrentCase(reduxCaseStruct));

    let reduxStruct = null;

    const fetchedPersons = bigCaseDescrip.persons;
    fetchedPersons.forEach((person) => {
      if (!this.alreadyLoadedPerson(person.personId)) {
        reduxStruct = this.convertPersonApiStructIntoReduxStruct(person);
        reduxStruct.leadTemperature = caseApiStruct.leadTemperature;
        this.store.dispatch(addPerson(reduxStruct));
      }
    });

    const fetchedTasks = bigCaseDescrip.tasks;
    fetchedTasks.forEach((task) => {
      if (!this.alreadyLoadedTask(task.taskId)) {
        // All of the tasks in the bigCaseDescrip were related to the subject, rather than a visible firm
        // so we hope we don't need to do the next couple lines

        this.store.dispatch(addTask(task));
      }
    });

    const emergencyContacts = bigCaseDescrip.emergencyContacts;
    reduxStruct = this.convertContactsApiStructIntoReduxStruct(emergencyContacts);
    this.store.dispatch(addOrUpdateContacts({ clientId: clientId, contacts: reduxStruct }));
    emergencyContacts.forEach((ec) => {
      if (ec.inactive) {
        // must mark the *person* inactive, since that's where our code expects to find it
        const contact = DA.getPersonById(ec.contactId);
        const augmentedContact = { ...contact, inactive: true };
        this.store.dispatch(updatePerson(augmentedContact));
      }
    });

    const clientNotes = bigCaseDescrip.clientNotes;
    if (clientNotes.length > 0) {
      this.addOrUpdateNotesOnPersons(clientNotes);
    }

    const statusHistory = bigCaseDescrip.statusHistory;
    reduxStruct = this.convertStatusHistoryArrayApiStructIntoReduxStruct(statusHistory);
    this.store.dispatch(addStatusTransitions(reduxStruct));

    const outreachHistory = bigCaseDescrip.outreachHistory; // to, from, toContact, fromContact
    reduxStruct = this.convertOutreachStructsIntoReduxStructs(outreachHistory);
    reduxStruct.forEach((struct) => {
      const personId = struct.direction === 'To' ? struct.receiverId : struct.senderId;
      this.store.dispatch(addOutreaches({ personId, outreaches: [struct] }));
    });

    const dls = bigCaseDescrip.distributionLists;
    dls.forEach((dlApiStruct) => {
      reduxStruct = this.convertDlApiStructIntoReduxStruct(dlApiStruct);
      this.store.dispatch(addDistributionList(reduxStruct));
    });

    const missives = bigCaseDescrip.missives;
    missives.forEach((missiveApiStruct) => {
      reduxStruct = this.convertMissiveApiStructIntoReduxStruct(missiveApiStruct);
      this.store.dispatch(addMissive(reduxStruct));
    });

    const callRailCalls = bigCaseDescrip.callRailCalls;
    callRailCalls.forEach((callRailCallApiStruct) => {
      this.addCallRailCall(callRailCallApiStruct);
    });

    const firms = bigCaseDescrip.firms;
    firms.forEach((firmApiStruct) => {
      reduxStruct = this.convertFirmApiStructIntoReduxStruct(firmApiStruct);
      this.store.dispatch(addFirm(reduxStruct));
    });

    const referrals = bigCaseDescrip.referrals;
    referrals.forEach((referralApiStruct) => {
      this.addReferral(referralApiStruct);
    });

    const campaigns = bigCaseDescrip.campaigns;
    campaigns.forEach((campaignApiStruct) => {
      reduxStruct = this.convertCampaignApiStructIntoReduxStruct(campaignApiStruct);
      this.store.dispatch(addCampaign(reduxStruct));
    });

    const referralNotes = bigCaseDescrip.referralNotes;
    reduxStruct = this.convertNoteApiStructsIntoReduxStructs(referralNotes);
    this.addOrUpdateNotesOnReferrals(referralNotes);

    const insuranceInfos = bigCaseDescrip.insuranceInfos;
    insuranceInfos.forEach((insuranceInfoApiStruct) => {
      reduxStruct = this.convertInsuranceInfoApiStructIntoReduxStruct(insuranceInfoApiStruct);
      this.store.dispatch(addInsuranceInfo(reduxStruct));
    });

    const vobResolutions = bigCaseDescrip.vobResolutions;
    reduxStruct = this.convertVobsApiStructIntoReduxStruct(clientId, vobResolutions);
    this.store.dispatch(addVobResolutions(reduxStruct));

    const taskableGroups = bigCaseDescrip.taskableGroups;
    taskableGroups.forEach((groupApiStruct) => {
      this.store.dispatch(addTaskableGroup(groupApiStruct));
    });
  }

  loadNotesOnPerson(clientId, completionFunc) {
    const uri = `/api/note/onPerson/${clientId}`;
    const request = createStdRequest(uri);
    const goodResponseAction = (responseJson) => {
      const noteApiStructs = responseJson;
      // Monitor.recordReceivedStruct('notesOnPerson', clientId, noteApiStructs);
      this.addOrUpdateNotesOnPersons(noteApiStructs);
      completionFunc();
    };
    apiFetch(this.page, request, goodResponseAction);
  }

  addOrUpdateNotesOnPersons(noteApiStructs) {
    const reduxStructs = this.convertNoteApiStructsIntoReduxStructs(noteApiStructs);
    this.store.dispatch(addOrUpdateNotesOnPersons(reduxStructs));
  }

  addOrUpdateNotesOnCases(noteApiStructs) {
    const reduxStructs = this.convertNoteApiStructsIntoReduxStructs(noteApiStructs);
    this.store.dispatch(addOrUpdateNotesOnCases(reduxStructs));
  }

  addOrUpdateNotesOnFirms(noteApiStructs) {
    const reduxStructs = this.convertNoteApiStructsIntoReduxStructs(noteApiStructs);
    this.store.dispatch(addOrUpdateNotesOnFirms(reduxStructs));
  }

  addOrUpdateNotesOnReferrals(noteApiStructs) {
    const reduxStructs = this.convertNoteApiStructsIntoReduxStructs(noteApiStructs);
    this.store.dispatch(addOrUpdateNotesOnReferrals(reduxStructs));
  }

  addOrUpdateNotesOnTreatmentPrograms(noteApiStructs) {
    const reduxStructs = this.convertNoteApiStructsIntoReduxStructs(noteApiStructs);
    this.store.dispatch(addOrUpdateNotesOnTreatmentPrograms(reduxStructs));
  }

  addOrUpdateNotesOnFacilities(noteApiStructs) {
    const reduxStructs = this.convertNoteApiStructsIntoReduxStructs(noteApiStructs);
    this.store.dispatch(addOrUpdateNotesOnFacilities(reduxStructs));
  }

  addOrUpdateNotesOnMissives(noteApiStructs) {
    const reduxStructs = this.convertNoteApiStructsIntoReduxStructs(noteApiStructs);
    this.store.dispatch(addOrUpdateNotesOnMissives(reduxStructs));
  }

  addOrUpdateNotesOnOutreaches(noteApiStructs) {
    const reduxStructs = this.convertNoteApiStructsIntoReduxStructs(noteApiStructs);
    this.store.dispatch(addOrUpdateNotesOnOutreaches(reduxStructs));
  }

  addOrUpdateNotesOnNotes(noteApiStructs) {
    const reduxStructs = this.convertNoteApiStructsIntoReduxStructs(noteApiStructs);
    this.store.dispatch(addOrUpdateNotesOnNotes(reduxStructs));
  }

  addOrUpdateNotesOnInsurance(noteApiStructs) {
    const reduxStructs = this.convertNoteApiStructsIntoReduxStructs(noteApiStructs);
    this.store.dispatch(addOrUpdateNotesOnInsurance(reduxStructs));
  }

  addOrUpdateNotesOnVobResolutions(noteApiStructs) {
    const reduxStructs = this.convertNoteApiStructsIntoReduxStructs(noteApiStructs);
    this.store.dispatch(addOrUpdateNotesOnVobResolutions(reduxStructs));
  }

  addOrUpdateNotesOnStatusTransitions(noteApiStructs) {
    const reduxStructs = this.convertNoteApiStructsIntoReduxStructs(noteApiStructs);
    this.store.dispatch(addOrUpdateNotesOnStatusTransitions(reduxStructs));
  }

  addOrUpdateNotesOnExtensions(noteApiStructs) {
    const reduxStructs = this.convertNoteApiStructsIntoReduxStructs(noteApiStructs);
    this.store.dispatch(addOrUpdateNotesOnExtensions(reduxStructs));
  }

  addOrUpdateNotesOnDischarges(noteApiStructs) {
    const reduxStructs = this.convertNoteApiStructsIntoReduxStructs(noteApiStructs);
    this.store.dispatch(addOrUpdateNotesOnDischarges(reduxStructs));
  }

  addOrUpdateNotesOnInquiries(noteApiStructs) {
    const reduxStructs = this.convertNoteApiStructsIntoReduxStructs(noteApiStructs);
    this.store.dispatch(addOrUpdateNotesOnInquiries(reduxStructs));
  }

  addOrUpdateNotesOnCallRailCalls(noteApiStructs) {
    const reduxStructs = this.convertNoteApiStructsIntoReduxStructs(noteApiStructs);
    this.store.dispatch(addOrUpdateNotesOnCallRailCalls(reduxStructs));
  }

  convertNoteApiStructsIntoReduxStructs(notesApiStruct) {
    /*
        output should look like:
        [
        {
            noteId: 123,
            date: <some DateTime>,
            targetId: 42,
            summary: "some possibly very extended text ...",
            content: "some possibly very extended text extended extended extended extended extended extended extended extended extended"
        },
        {
            noteId: 126,
            date: <some DateTime>,
            targetId: 42,
            summary: "Not an addict!",
            content: "John is not known to be addicted to anything!"
        }
        ]

        API-generated input will be like:
        [
        {
            NoteId = note.NoteId,
            Date = Util.ConvertDateTimeToIso810(note.Date),
            AuthorId = author.PersonId,
            SubjectId = client.PersonId,
            // TODO use summary, once it's in DB
            Summary = note.Content.Substring(0, 100),
            Content = note.Content
        } ...
        ]
        */
    if (notesApiStruct.length > 0 && typeof notesApiStruct[0].date === 'string') {
      notesApiStruct = notesApiStruct.map((note) => this.convertNoteApiStructIntoReduxStruct(note));
    }
    return notesApiStruct;
  }

  convertNoteApiStructIntoReduxStruct(note) {
    const targetId = note.subjectId;
    return { ...note, targetId };
  }

  loadFirm(firmId, completionFunc) {
    const uri = `/api/firm/${firmId}`;
    const request = createStdRequest(uri);
    const goodResponseAction = (responseJson) => {
      const firmApiStruct = responseJson;
      const reduxStruct = this.convertFirmApiStructIntoReduxStruct(firmApiStruct);
      this.store.dispatch(addFirm(reduxStruct));
      completionFunc();
    };
    apiFetch(this.page, request, goodResponseAction);
  }

  loadFirmAsReferrerOrg(firmId, completionFunc) {
    const uri = `/api/firm/${firmId}`;
    const request = createStdRequest(uri);
    const goodResponseAction = (responseJson) => {
      const firmApiStruct = responseJson;
      // Monitor.recordReceivedStruct('firmAsReferrerOrg', firmId, firmApiStruct);
      const reduxStruct = this.convertFirmApiStructIntoReduxStruct(firmApiStruct);
      this.store.dispatch(addFirm(reduxStruct));

      // now arrange to load all the referrals received from this firm
      const childWorkList = reduxStruct.referralFromIds.map((refId) => {
        return { objType: 'referral', id: refId };
      });
      this.processWorkList(childWorkList, completionFunc);
    };
    apiFetch(this.page, request, goodResponseAction);
  }

  convertFirmApiStructIntoReduxStruct(firmApiStruct) {
    /*
        output should look like:
        {
            firmId: 2,
            name: "Acme Recovery",
            firmType: "Treatment Center"},
            description: "Some text goes here",
        }

        API-generated input will be like:
        {
            FirmId = firm.FirmId,
            Name = firm.Name,
            FacebookUrl = firm.FacebookUrl,
            HomePageUrl = firm.HomePageUrl,
            FirmType = firm.FirmType.Name,
            Description = descriptionStr
        }
        */
    // They're a perfect match, so just:
    return firmApiStruct;
  }

  loadReferral(referralId, completionFunc) {
    const uri = '/api/referral/' + referralId;
    const request = createStdRequest(uri);
    const goodResponseAction = (responseJson) => {
      const referralApiStruct = responseJson;
      // Monitor.recordReceivedStruct('referral', referralId, referralApiStruct);
      const reduxStruct = this.convertReferralApiStructIntoReduxStruct(referralApiStruct);
      this.store.dispatch(addReferral(reduxStruct));
      const childWorkList = [
        { objType: 'person', id: reduxStruct.referreeId },
        { objType: 'person', id: reduxStruct.referrerId },
        { objType: 'person', id: reduxStruct.receiverId },
        { objType: 'firm', id: reduxStruct.referringOrgId },
        // { objType: 'firmAsReferrerOrg', id: reduxStruct.referringOrgId },
        { objType: 'campaign', id: reduxStruct.campaignId },
        { objType: 'notesAboutReferral', id: referralId },
      ];
      this.processWorkList(childWorkList, completionFunc);
    };
    apiFetch(this.page, request, goodResponseAction);
  }

  addReferral(referralApiStruct) {
    const reduxStruct = this.convertReferralApiStructIntoReduxStruct(referralApiStruct);
    this.store.dispatch(addReferral(reduxStruct));
  }

  convertReferralApiStructIntoReduxStruct(referralApiStruct) {
    /*
        output should look like:
        {
            referralId,
            date: this.now - (this.oneDay * 6),
            referreeId: 42,
            referrerId: 77,
            referringOrgId: 35,
            receiverId: 16,
            receivingOrgId: 2,
            communicationType: "Phone Call",
            campaignId: 0,
            salesStage: "Initial Contact"
        }

        API-generated input looks like:
        {
            public int ReferralId { get; set; }
            public long ContactDate { get; set; }
            public string CommunicationType { get; set; }
            public int ReferreeId { get; set; }
            public int ReferringPersonId { get; set; }
            public int ReferringOrgId { get; set; }
            public int ClientContactId { get; set; }
            public string ClientContactRelationship { get; set; }
            public int ReceivingUserPersonId { get; set; }
            public int ReceivingOrgId { get; set; }
            public int CampaignId { get; set; }
            public string SalesStage { get; set; }
            public List<string> SubstanceNameStarts { get; set; } = new List<string>();
        }
        */
    if (Object.keys(referralApiStruct).indexOf('date') >= 0) {
      return referralApiStruct;
    } else {
      return {
        ...referralApiStruct,
        date: referralApiStruct.contactDate,
        referrerId: referralApiStruct.referringPersonId,
        receiverId: referralApiStruct.receivingUserPersonId,
      };
    }
  }

  loadEmergencyContactsOfClient(clientId, completionFunc) {
    const uri = '/api/person/' + clientId + '/contact';
    const request = createStdRequest(uri);
    const goodResponseAction = (responseJson) => {
      const contactsApiStruct = responseJson;
      // Monitor.recordReceivedStruct('emergencyContacts', clientId, contactsApiStruct);
      const reduxStruct = this.convertContactsApiStructIntoReduxStruct(contactsApiStruct);
      this.store.dispatch(addOrUpdateContacts({ clientId: clientId, contacts: reduxStruct }));
      contactsApiStruct.forEach((contactStruct) => {
        if (contactStruct.inactive) {
          // must mark the *person* inactive, since that's where our code expects to find it
          const contact = DA.getPersonById(contactStruct.contactId);
          const augmentedContact = { ...contact, inactive: true };
          this.store.dispatch(updatePerson(augmentedContact));
        }
      });

      const childWorkList = reduxStruct.map((ctct) => {
        return { objType: 'person', id: ctct.personId };
      });
      this.processWorkList(childWorkList, completionFunc);
    };
    apiFetch(this.page, request, goodResponseAction);
  }

  convertContactsApiStructIntoReduxStruct(contactsApiStruct) {
    /* output must look like:
                    [
                        {
                            personId: 524,
                            relationship: "Mother-in-law",
                            description: "Crotchety",
                            inactive: true,
                        },
                        {
                            personId: 525,
                            relationship: "Friend",
                            description: "Codependent",
                            inactive: false,
                        }
                    ]
        */

    /* API-fetched input looks like:
        [
            {
              int ClientContactId
              int ClientId
              int ContactId
              string Relationship
              string Description
              int ViewingFirmId
              bool Inactive
            },
            {
              int ClientContactId
              int ClientId
              int ContactId
              string Relationship
              string Description
              int ViewingFirmId
              bool Inactive
            }
        ]
        */
    if (contactsApiStruct.length === 0 || Object.keys(contactsApiStruct[0]).indexOf('personId') >= 0) {
      return contactsApiStruct;
    }

    const contacts = contactsApiStruct.map((cr) => {
      return {
        personId: cr.contactId,
        relationship: cr.relationship,
        description: cr.description,
        inactive: cr.inactive,
      };
    });
    return contacts;
  }

  loadTasksForSubject(subjectId, completionFunc) {
    const uri = '/api/task/onSubject/' + subjectId;
    const request = createStdRequest(uri);
    const goodResponseAction = (responseJson) => {
      const taskInfoApiStructs = responseJson;
      // Monitor.recordReceivedStruct('tasksForSubject', subjectId, taskInfoApiStructs);
      const childWorkList = [];
      taskInfoApiStructs.forEach((taskInfo) => {
        this.store.dispatch(addTask(taskInfo));
        childWorkList.push({ objType: 'person', id: taskInfo.ownerId });
        childWorkList.push({ objType: 'person', id: taskInfo.subjectId });
        childWorkList.push({ objType: 'person', id: taskInfo.assigneeId });
        childWorkList.push({ objType: 'assignableGroup', id: taskInfo.assignedGroupId });
        childWorkList.push({ objType: 'person', id: taskInfo.claimantId });
      });
      this.processWorkList(childWorkList, completionFunc);
    };
    apiFetch(this.page, request, goodResponseAction);
  }

  deleteInquiryWithId(inquiryId) {
    this.store.dispatch(removeInquiry(inquiryId));
  }

  loadTreatmentProgram(treatmentProgramId, completionFunc) {
    const firmId = DA.getFirmId();
    const uri = `/api/firm/${firmId}/treatmentProgram/${treatmentProgramId}`;
    const request = createStdRequest(uri);
    const goodResponseAction = (responseJson) => {
      const programApiStruct = responseJson;
      const reduxStruct = this.convertTreatmentProgramApiStructIntoReduxStruct(programApiStruct);
      this.store.dispatch(addTreatmentProgram(reduxStruct));
      const childWorkList = [
        { objType: 'firm', id: reduxStruct.firmId },
        { objType: 'facility', id: reduxStruct.facilityId },
      ];
      this.processWorkList(childWorkList, completionFunc);
    };
    apiFetch(this.page, request, goodResponseAction);
  }

  convertTreatmentProgramApiStructIntoReduxStruct(programApiStruct) {
    /*
        output should look like:
        {
            treatmentProgramId,
            name: "Alcohol Rehab",
            firmId: 2,
            description: "Alcohol Rehab at Acme",
            facilityId: 22
        }

        API-generated input looks like
        {
            public int TreatmentProgramId { get; set; }
            public string Name { get; set; }
            public string Description { get; set; }
            public int FirmId { get; set; }
            public int FacilityId { get; set; }
        }
        */
    // They're a perfect match, so just:
    return programApiStruct;
    /*
                if () {
                    return programApiStruct;
                } else {
                    return {
                        treatmentProgramId: programApiStruct.treatmentProgramId,
                        name: programApiStruct.name,
                        firmId: programApiStruct.firmId,
                        description: programApiStruct.description,
                        facilityId: programApiStruct.facilityId
                    };
                }
        */
  }

  loadFacility(facilityId, completionFunc) {
    const firmId = DA.getFirmId();
    const uri = `/api/firm/${firmId}/facility/${facilityId}`;
    const request = createStdRequest(uri);
    const goodResponseAction = (responseJson) => {
      const facilityApiStruct = responseJson;
      const reduxStruct = this.convertFacilityApiStructIntoReduxStruct(facilityApiStruct);
      this.store.dispatch(addFacility(reduxStruct));
      const childWorkList = [{ objType: 'firm', id: facilityApiStruct.firmId }];
      this.processWorkList(childWorkList, completionFunc);
    };
    apiFetch(this.page, request, goodResponseAction);
  }

  convertFacilityApiStructIntoReduxStruct(facilityApiStruct) {
    /*
        output should look like:
        {
            facilityId: facilityId,
            name: "Home Away at Acme"
        }

        API-generated input looks like:
        {
            public int FacilityId { get; set; }
            public string Name { get; set; }
            public int FirmId { get; set; }
            public string PictureUrl { get; set; }
            public AddressApiStruct Address { get; set; }
            public List<PhoneApiStruct> PhoneNumbers { get; set; } = new List<PhoneApiStruct>();
        }
        */
    return {
      facilityId: facilityApiStruct.facilityId,
      name: facilityApiStruct.name,
    };
  }

  loadOutreachHistoryForPerson(page, personId, completionFunc) {
    const firmId = DA.getFirmId();
    const uri = `/api/firm/${firmId}/person/${personId}/outreach`;
    const request = createStdRequest(uri);
    const goodResponseAction = (responseJson) => {
      const outreachHistory = responseJson; // to, from, toContact, fromContact
      // Monitor.recordReceivedStruct('outreachHistory', personId, outreachHistory);
      const reduxStruct = this.convertOutreachStructsIntoReduxStructs(outreachHistory);
      this.store.dispatch(addOutreaches({ personId, outreaches: reduxStruct }));
      const childWorkList = [];
      reduxStruct.forEach((outreachRec) => {
        childWorkList.push({ objType: 'person', id: outreachRec.senderId });
        childWorkList.push({ objType: 'person', id: outreachRec.receiverId });
        childWorkList.push({ objType: 'dl', id: outreachRec.dlId });
        childWorkList.push({ objType: 'missive', id: outreachRec.missiveId });
      });
      this.setPage(page);
      this.processWorkList(childWorkList, completionFunc);
    };
    apiFetch(this.page, request, goodResponseAction);
  }

  convertOutreachStructsIntoReduxStructs(outreachApiStructArray) {
    /* output should like like:
          [
                {
                    outreachId: 214,
                    date: this.now - (this.oneDay * 5),
                    descrip: "Texted John about his availability",
                    senderId: 6,
                    receiverId: 42,
                    direction: "To",
                    missiveId: 220,
                },
                {
                    outreachId: 334,
                    date: this.now - (this.oneDay * 4),
                    descrip: "Anna called about John's availability",
                    senderId: 88,
                    receiverId: 6,
                    direction: "From",
                    missiveId: 350,
                }
          ]

        API-generated input will look like:
        [
            {
                public int OutreachId { get; set; }
                public long Date { get; set; }
                public int FirmId { get; set; }
                public int SenderId { get; set; }
                public int SenderFirmId { get; set; }
                public int ReceiverId { get; set; }
                public int ReceiverFirmId { get; set; }
                public int DlId { get; set; }
                public int MissiveId { get; set; }
                public MissiveApiStruct Missive { get; set; }
            },
            {
                public int OutreachId { get; set; }
                public long Date { get; set; }
                public int FirmId { get; set; }
                public int SenderId { get; set; }
                public int SenderFirmId { get; set; }
                public int ReceiverId { get; set; }
                public int ReceiverFirmId { get; set; }
                public int DlId { get; set; }
                public int MissiveId { get; set; }
                public MissiveApiStruct Missive { get; set; }
            }
        ]
        */
    /*
                if (outreachApiStructArray &&
                    outreachApiStructArray.length > 0 &&
                    Object.keys(outreachApiStructArray[0]).indexOf("MissiveId") >= 0 ) {
                    return outreachApiStructArray.map(outreach => { return {
                        outreachId: outreach.outreachId,
                        date: outreach.date,
                        firmId: outreach.firmId,
                        senderId: outreach.senderId,
                        receiverId: outreach.receiverId,
                        dlId: outreach.dlId,
                        missiveId: outreach.missiveId
                    }});
                } else {
                    return outreachApiStructArray;
                }
        */
    return outreachApiStructArray.map((outreach) => this.convertOutreachStructIntoReduxStruct(outreach));
  }

  convertOutreachStructIntoReduxStruct(outreachApiStruct) {
    const direction = outreachApiStruct.receiverFirmId === DA.getFirmId() ? 'From' : 'To';
    return { ...outreachApiStruct, direction };
  }

  loadStatusHistoryForClient(personId, completionFunc) {
    const uri = '/api/client/' + personId + '/statusTransition';
    const request = createStdRequest(uri);
    const goodResponseAction = (responseJson) => {
      const statusHistoryApiStruct = responseJson;
      // Monitor.recordReceivedStruct('statusHistory', personId, statusHistoryApiStruct);
      const reduxStruct = this.convertStatusHistoryArrayApiStructIntoReduxStruct(statusHistoryApiStruct);
      this.store.dispatch(addStatusTransitions(reduxStruct));
      completionFunc();
    };
    apiFetch(this.page, request, goodResponseAction);
  }

  addStatusTransitions(transitions) {
    this.store.dispatch(addStatusTransitions(transitions));
  }

  convertStatusHistoryArrayApiStructIntoReduxStruct(historyApiStructArray) {
    /*
        output wants to look like:
        [
          {
            transitionId: 62,
            date: this.now - (this.oneDay * 6),
            firmId: 2,
            subjectId: 42,
            oldStatus: "None",
            newStatus: "Prospect",
            transitionDescrip: "Entered as Prospect"
          },
          {
            transitionId: 5,
            date: this.now - (this.oneDay * 4),
            firmId: 2,
            subjectId: 667,
            oldStatus: "Prospect",
            newStatus: "Client",
            transitionDescrip: "Accepted as Client"
          }
        ]

        input looks like:
        [
          {
            public int StatusTransitionId { get; set; }
            public int FirmId { get; set; }
            public int ClientId { get; set; }
            public string Date { get; set; }
            public string NewStatus { get; set; }
          },
          {
            public int StatusTransitionId { get; set; }
            public int FirmId { get; set; }
            public int ClientId { get; set; }
            public string Date { get; set; }
            public string NewStatus { get; set; }
          }
        ]

        */
    if (
      historyApiStructArray &&
      historyApiStructArray.length > 0 &&
      Object.keys(historyApiStructArray[0]).indexOf('transitionDescrip') >= 0
    ) {
      return historyApiStructArray;
    } else {
      return historyApiStructArray.map((st) => {
        return {
          transitionId: st.statusTransitionId,
          date: st.date,
          firmId: st.firmId,
          subjectId: st.clientId,
          newStatus: st.newStatus,
          transitionDescrip: this.transitionDescripForStatus(st.newStatus),
        };
      });
    }
  }

  transitionDescripForStatus(status) {
    switch (status) {
      case 'None':
        return "Initialized with status 'None'";
      case 'Prospect':
        return 'Entered system as Prospect';
      case 'Client':
        return 'Admitted as Client';
      case 'Alum':
        return 'Graduated to Alum';
      default:
        return 'Unknown progress to status: ' + status;
    }
  }

  loadDl(dlId, completionFunc) {
    const firmId = DA.getFirmId();
    const uri = `/api/firm/${firmId}/distributionList/${dlId}`;
    const request = createStdRequest(uri);
    const goodResponseAction = (responseJson) => {
      const dlApiStruct = responseJson;
      // Monitor.recordReceivedStruct('dl', dlId, dlApiStruct);
      const reduxStruct = this.convertDlApiStructIntoReduxStruct(dlApiStruct);
      this.store.dispatch(addDistributionList(reduxStruct));
      const childWorkList = dlApiStruct.memberIds.map((id) => {
        return { objType: 'person', id };
      });
      this.processWorkList(childWorkList, completionFunc);
    };
    apiFetch(this.page, request, goodResponseAction);
  }

  convertDlApiStructIntoReduxStruct(dlApiStruct) {
    /*
        output should look like:
        {
            distributionListId: 776,
            firmId: 2,
            name: "Alumni,
            description: "A mailing list for all our alumni",
            memberIds: [42]
        }

        API-generated input will look like
        {
            public int DistributionListId { get; set; }
            public string Name { get; set; }
            public string Description { get; set; }
            public int FirmId { get; set; }
            public List<int> MemberIds = new List<int>();
        }
        */
    // since they're a perfect match, lets just return what we were given
    return dlApiStruct;
    /*
                return {
                    distributionListId: 776,
                    firmId: 2,
                    name: "Alumni,
                    description: "A mailing list for all our alumni",
                    memberIds: [42]
                };
        */
  }

  loadMissive(missiveId, completionFunc) {
    const firmId = DA.getFirmId();
    const uri = `/api/firm/${firmId}/missive/${missiveId}`;
    const request = createStdRequest(uri);
    const goodResponseAction = (responseJson) => {
      const missiveApiStruct = responseJson;
      // Monitor.recordReceivedStruct('missive', missiveId, missiveApiStruct);
      const reduxStruct = this.convertMissiveApiStructIntoReduxStruct(missiveApiStruct);
      this.store.dispatch(addMissive(reduxStruct));
      const childWorkList = [
        { objType: 'person', id: reduxStruct.creatorId },
        { objType: 'firm', id: reduxStruct.firmId },
        { objType: 'callRailCall', id: reduxStruct.callRailCallId || 0 },
      ];
      this.processWorkList(childWorkList, completionFunc);
    };
    apiFetch(this.page, request, goodResponseAction);
  }

  loadCallRailCall(callRailCallId, completionFunc) {
    const uri = `/api/callrail/${callRailCallId}`;
    const request = createStdRequest(uri);
    const goodResponseAction = (responseJson) => {
      const callRailCallApiStruct = responseJson;
      // Monitor.recordReceivedStruct('callRailCall', callRailCallId, callRailCallApiStruct);
      this.addCallRailCall(callRailCallApiStruct);
      const childWorkList = [{ objType: 'person', id: callRailCallApiStruct.userPersonId }];
      this.processWorkList(childWorkList, completionFunc);
    };
    apiFetch(this.page, request, goodResponseAction);
  }

  convertMissiveApiStructIntoReduxStruct(missiveApiStruct) {
    /*
        output should look like:
        {
              missiveId: missiveId,
              creatorId: 228,
              firmId: 2,
              missiveType: "Mass Email",
              name: "New Facility Flier",
              description: "Publicity note about our new facility",
              content: "This is supposed to somehow represent ...",
              callRailCallId: 14,
        }

        API-generated input should be like:
        {
            public int MissiveId { get; set; }
            public string Name { get; set; }
            public string Description { get; set; }
            public int FirmId { get; set; }
            public int CreatorId { get; set; }
            public string TypeStr { get; set; }
            public string Content { get; set; }
            public int CallRailCallId { get; set; }
        }
        */
    if (Object.keys(missiveApiStruct).indexOf('missiveType') >= 0) {
      return missiveApiStruct;
    } else {
      return {
        missiveId: missiveApiStruct.missiveId,
        creatorId: missiveApiStruct.creatorId,
        firmId: missiveApiStruct.firmId,
        missiveType: missiveApiStruct.typeStr,
        name: missiveApiStruct.name,
        description: missiveApiStruct.description,
        content: missiveApiStruct.content,
        callRailCallId: missiveApiStruct.callRailCallId,
      };
    }
  }

  loadCampaign(campaignId, completionFunc) {
    const firmId = DA.getFirmId();
    const uri = `/api/firm/${firmId}/campaign/${campaignId}`;
    const request = createStdRequest(uri);
    const goodResponseAction = (responseJson) => {
      const campaignApiStruct = responseJson;
      // Monitor.recordReceivedStruct('campaign', campaignId, campaignApiStruct);
      const reduxStruct = this.convertCampaignApiStructIntoReduxStruct(campaignApiStruct);
      this.store.dispatch(addCampaign(reduxStruct));
      const childWorkList = [{ objType: 'firm', id: reduxStruct.firmId }];
      this.processWorkList(childWorkList, completionFunc);
    };
    apiFetch(this.page, request, goodResponseAction);
  }

  convertCampaignApiStructIntoReduxStruct(campaignApiStruct) {
    /*
        output should look like:
        {
            campaignId: campaignId,
            firmId: 2,
            name: "Publicize New Facility",
            startDate: <some DateTime>,
            endDate: <some later DateTime>,
            description: "Campaign to increase visibility by publicizing new facility",
        }

        API-generated input should be like:
        {
            public int CampaignId { get; set; }
            public int FirmId { get; set; }
            public string Name { get; set; }
            public string StartDate { get; set; }
            public string EndDate { get; set; }
            public string Description { get; set; }
        }
        */
    return campaignApiStruct;
  }

  loadInsuranceInfo(clientId, insuranceInfoId, completionFunc) {
    const uri = `/api/person/${clientId}/insuranceInfo/${insuranceInfoId}`;
    const request = createStdRequest(uri);
    const goodResponseAction = (responseJson) => {
      const insuranceInfoApiStruct = responseJson;
      // Monitor.recordReceivedStruct('insuranceInfo', insuranceInfoId, insuranceInfoApiStruct);
      const reduxStruct = this.convertInsuranceInfoApiStructIntoReduxStruct(insuranceInfoApiStruct);
      this.store.dispatch(addInsuranceInfo(reduxStruct));
      const childWorkList = [{ objType: 'vobs', id: clientId, id2: insuranceInfoId }];
      this.processWorkList(childWorkList, completionFunc);
    };
    apiFetch(this.page, request, goodResponseAction);
  }

  convertInsuranceInfoApiStructIntoReduxStruct(insuranceInfoApiStruct) {
    /*
        output should be like:
        {
            insuranceInfoId,
            personId: clientId,
            provider: "Kaiser",
            type: "Unknown",
            coverageLevel: "Unknown",
            groupNumber: "GB00654",
            policyNumber: "B734889",
        }

        API-generated input will be like:
        {
            public int InsuranceInfoId { get; set; }
            public int PersonId { get; set; }
            public string ProviderName { get; set; }
            public string TypeName { get; set; }
            public string CoverageLevelName { get; set; }
            public string GroupNumber { get; set; }
            public string PolicyNumber { get; set; }
        }
        */
    if (Object.keys(insuranceInfoApiStruct).indexOf('coverageLevel') >= 0) {
      return insuranceInfoApiStruct;
    } else {
      return {
        insuranceInfoId: insuranceInfoApiStruct.insuranceInfoId,
        personId: insuranceInfoApiStruct.personId,
        provider: insuranceInfoApiStruct.providerName,
        type: insuranceInfoApiStruct.typeName,
        coverageLevel: insuranceInfoApiStruct.coverageLevelName,
        groupNumber: insuranceInfoApiStruct.groupNumber,
        policyNumber: insuranceInfoApiStruct.policyNumber,
      };
    }
  }

  loadVobs(clientId, insuranceInfoId, completionFunc) {
    const uri = `/api/person/${clientId}/insuranceInfo/${insuranceInfoId}/vob`;
    const request = createStdRequest(uri);
    const goodResponseAction = (responseJson) => {
      const vobsApiStruct = responseJson;
      // Monitor.recordReceivedStruct('vobs', insuranceInfoId, vobsApiStruct);
      const reduxStruct = this.convertVobsApiStructIntoReduxStruct(clientId, vobsApiStruct);
      this.store.dispatch(addVobResolutions(reduxStruct));
      // the following extra work is almost certainly already done, but no harm in checking
      const childWorkList = [
        { objType: 'person', id: clientId },
        { objType: 'insuranceInfo', id: insuranceInfoId, id2: clientId },
      ];
      this.processWorkList(childWorkList, completionFunc);
    };
    apiFetch(this.page, request, goodResponseAction);
  }

  convertVobsApiStructIntoReduxStruct(clientId, vobApiStruct) {
    /*
        output should be like:
        [
          {
            vobId: 9967,
            personId: clientId,
            date: 12357003648,
            resolutionCode: "87EF006",
            insuranceInfoId: insuranceInfoId
          }
        ]

        API-generated input will be like:
        [
          {
            VobResolutionId  = vob.VobResolutionId,
            Date = Util.ConvertDateTimeToIso8601(vob.Date),
            InsuranceInfoId = vob.PersonInsuranceView.InsuranceInfo.InsuranceInfoId,
            ResolutionCode = vob.ResolutionCode
          }
        ]
        */
    if (vobApiStruct.length === 0 || Object.keys(vobApiStruct[0]).indexOf('vobId') >= 0) {
      return vobApiStruct;
    } else {
      const modified = vobApiStruct.map((vob) => {
        return {
          vobId: vob.vobResolutionId,
          personId: clientId,
          date: vob.date,
          resolutionCode: vob.resolutionCode,
          insuranceInfoId: vob.insuranceInfoId,
        };
      });
      return modified;
    }
  }

  convertAlertSubscriptionToReduxForm(subscripApiStruct) {
    /*
        API-generated input will be like:
        [
          {
            public int AlertSubscriptionId { get; set; }
            public int AlertableEventId { get; set; }
            public string UserId { get; set; }
            public int FirmId { get; set; }
            public bool ViaText { get; set; }
            public bool ViaEmail { get; set; }
            public bool ViaTask { get; set; }
          }
        ]

        output should be like:
        [
          {
            id: 467,
            eventId: 9967,
            userId: <big string>,
            viaText: T/F,
            viaEmail: T/F,
            viaTask: T/F,
          }
        ]
        */
    const { alertSubscriptionId, alertableEventId, userId, viaText, viaEmail, viaTask } = subscripApiStruct;
    return {
      id: alertSubscriptionId,
      eventId: alertableEventId,
      userId,
      viaText,
      viaEmail,
      viaTask,
    };
  }

  loadReferrerInfo(page, firmId, completionCallback) {
    if (this.haveLoadedReferrerInfo()) {
      completionCallback();
      return;
    }

    // We don't do the /visibleFirm call because we've already accomplished its purpose
    // in our action on the response to /bigDescrip

    // this is copied to the code below
    // this.fetchContactsAndCallCallback_2019_04_13(page, firmId, timeReportingCallback);

    // we want to do all of this in action on response to /bigDescrip request
    const uri = `/api/firm/${firmId}/visibleFirmContact`;
    const request = createStdRequest(uri);
    const actionOnGoodResponse = (json) => {
      // json is an array of FirmAndContactsApiStruct objects
      //   These look like: { firmId: n, contacts: [FirmContactApiStruct(augmented person struct)] }
      // The following line does two things:
      //   1) it produces a new array of {firmId: n, contactIds:[...]} objects
      //   2) along the way it stores in Redux the contact struct (augmented person struct) as a person
      const firmAndContactIdsArray = this.extractFirmAndContactIdsArrayForEach(json);
      this.store.dispatch(setVisibleFirmsWithContacts(firmAndContactIdsArray));
      reportTime('fetchCtcts', b4fetchContacts);
      completionCallback();
    };
    const b4fetchContacts = DateTime.local();
    apiFetch(page, request, actionOnGoodResponse);
  }

  extractFirmAndContactIdsArrayForEach(fetchedArray) {
    return fetchedArray.map((firmAndContactsApiStruct) =>
      this.extractFirmAndContactIdsArrayForOneOrg(firmAndContactsApiStruct)
    );
  }

  extractFirmAndContactIdsArrayForOneOrg(firmAndContactsApiStructForOneOrg) {
    // firmAndContactsApiStructForOneOrg.contacts.forEach(contact => this.createAndStoreReduxStructForFetchedContactApiStruct(contact));
    return {
      referralFirmId: firmAndContactsApiStructForOneOrg.firmId,
      contactIds: firmAndContactsApiStructForOneOrg.contacts.map((contact) => contact.personId),
    };
  }

  createAndStoreReduxStructForFetchedContactApiStruct(fetchedContact) {
    // we're going to store it in both the "persons" store and in a list in the "orgContacts" store
    // On 2019-12-6 I happened on this code and the above comment.  Seems like we don't actually do the latter.
    const reduxStruct = this.createReduxStructForFetchedPersonOrContactApiStruct(fetchedContact);
    this.store.dispatch(addPerson(reduxStruct));
    return reduxStruct;
  }

  createAndStoreReduxStructForFetchedPersonApiStruct(fetchedPerson) {
    // we're going to store it in both the "persons" store and in a list in the "orgContacts" store
    // On 2019-12-6 I happened on this code and the above comment.  Seems like we don't actually do the latter.
    const reduxStruct = this.createReduxStructForFetchedPersonOrContactApiStruct(fetchedPerson);
    this.store.dispatch(addPerson(reduxStruct));
    return reduxStruct;
  }

  alreadyLoadedOutreachHistoryWithFirm(visibleFirmId) {
    return DA.haveOutreachHistoryWithFirm(visibleFirmId);
  }

  loadOutreachHistoryWithFirm(page, visibleFirmId, completionFunc) {
    const viewingFirmId = DA.getFirmId();
    const uri = `/api/firm/${viewingFirmId}/outreach/withFirm/${visibleFirmId}`;
    const request = createStdRequest(uri);
    let allFirmOutreaches = [];
    const actionOnGoodResponse = (json) => {
      const outreachesToFirmGenerally = json.toFirm || [];
      const outreachesFromFirmGenerally = json.fromFirm || [];
      const outreachesToContact = json.toContact || [];
      const outreachesFromContact = json.fromContact || [];
      const outreachesToDl = json.toDl || [];
      outreachesToFirmGenerally.forEach((idAndOutreaches) => {
        const firmId = idAndOutreaches.id;
        const outreaches = idAndOutreaches.outreaches;
        allFirmOutreaches = [...allFirmOutreaches, ...outreaches];
        const reduxifiedOutreaches = this.convertOutreachStructsIntoReduxStructs(outreaches);
        this.store.dispatch(addOutreachesToFirm({ firmId, outreaches: reduxifiedOutreaches }));
      });
      outreachesFromFirmGenerally.forEach((idAndOutreaches) => {
        const firmId = idAndOutreaches.id;
        const outreaches = idAndOutreaches.outreaches;
        allFirmOutreaches = [...allFirmOutreaches, ...outreaches];
        const reduxifiedOutreaches = this.convertOutreachStructsIntoReduxStructs(outreaches);
        this.store.dispatch(addOutreachesFromFirm({ firmId, outreaches: reduxifiedOutreaches }));
      });
      outreachesToContact.forEach((idAndOutreaches) => {
        const contactId = idAndOutreaches.id;
        const outreaches = idAndOutreaches.outreaches;
        allFirmOutreaches = [...allFirmOutreaches, ...outreaches];
        const reduxifiedOutreaches = this.convertOutreachStructsIntoReduxStructs(outreaches);
        this.store.dispatch(addOutreachesToFirmContact({ contactId, outreaches: reduxifiedOutreaches }));
      });
      outreachesFromContact.forEach((idAndOutreaches) => {
        const contactId = idAndOutreaches.id;
        const outreaches = idAndOutreaches.outreaches;
        allFirmOutreaches = [...allFirmOutreaches, ...outreaches];
        const reduxifiedOutreaches = this.convertOutreachStructsIntoReduxStructs(outreaches);
        this.store.dispatch(addOutreachesFromFirmContact({ contactId, outreaches: reduxifiedOutreaches }));
      });
      outreachesToDl.forEach((idAndOutreaches) => {
        const dlId = idAndOutreaches.id;
        const outreaches = idAndOutreaches.outreaches;
        allFirmOutreaches = [...allFirmOutreaches, ...outreaches];
        const reduxifiedOutreaches = this.convertOutreachStructsIntoReduxStructs(outreaches);
        this.store.dispatch(addOutreachesToDl({ dlId, outreaches: reduxifiedOutreaches }));
      });
      const childWorkList = [];
      allFirmOutreaches.forEach((outreach) => {
        childWorkList.push({ objType: 'person', id: outreach.senderId });
        childWorkList.push({ objType: 'person', id: outreach.receiverId });
        childWorkList.push({ objType: 'dl', id: outreach.dlId });
        childWorkList.push({ objType: 'missive', id: outreach.missiveId });
      });
      this.setPage(page);
      this.processWorkList(childWorkList, completionFunc);
    };
    apiFetch(page, request, actionOnGoodResponse);
  }

  alreadyLoadedNotesAboutFirm(visibleFirmId) {
    return DA.haveNotesOnFirmWithId(visibleFirmId);
  }

  alreadyLoadedNotesAboutReferral(referralId) {
    return DA.haveNotesOnReferralWithId(referralId);
  }

  loadNotesAboutFirm(visibleFirmId, completionFunc) {
    const uri = `/api/note/onFirm/${visibleFirmId}`;
    const request = createStdRequest(uri);
    const goodResponseAction = (noteApiStructs) => {
      this.addOrUpdateNotesOnFirms(noteApiStructs);
      completionFunc();
    };
    apiFetch(this.page, request, goodResponseAction);
  }

  loadNotesAboutReferral(referralId, completionFunc) {
    const uri = `/api/note/onReferral/${referralId}`;
    const request = createStdRequest(uri);
    const goodResponseAction = (responseJson) => {
      const notesApiStruct = responseJson;
      // Monitor.recordReceivedStruct('notesAboutReferral', referralId, notesApiStruct);
      this.addOrUpdateNotesOnReferrals(notesApiStruct);
      completionFunc();
    };
    apiFetch(this.page, request, goodResponseAction);
  }

  alreadyLoadedTask(taskId) {
    return DA.haveTaskWithId(taskId);
  }

  alreadyLoadedTasksForFirm(visibleFirmId) {
    const viewingFirmId = DA.getFirmId();
    return DA.haveListOfTasksRelatedToVisibleFirm(viewingFirmId, visibleFirmId);
  }

  loadTasksForFirm(visibleFirmId, completionFunc) {
    const viewingFirmId = DA.getFirmId();
    const uri = `/api/task/firm/${viewingFirmId}/forFirm/${visibleFirmId}`;
    const request = createStdRequest(uri);
    const goodResponseAction = (responseJson) => {
      const taskInfoApiStructs = responseJson;
      this.store.dispatch(setOrgTasksForFirm({ visibleFirmId, tasks: taskInfoApiStructs }));
      const childWorkList = [];
      taskInfoApiStructs.forEach((taskInfo) => {
        childWorkList.push({ objType: 'person', id: taskInfo.ownerId });
        childWorkList.push({ objType: 'person', id: taskInfo.subjectId });
        childWorkList.push({ objType: 'person', id: taskInfo.assigneeId });
        childWorkList.push({ objType: 'person', id: taskInfo.claimantId });
      });
      this.processWorkList(childWorkList, completionFunc);
    };
    apiFetch(this.page, request, goodResponseAction);
  }

  alreadyLoadedAssignableGroup(taskableGroupId) {
    return DA.haveTaskableGroupWithId(taskableGroupId);
  }

  loadAssignableGroup(groupId, completionFunc) {
    const viewingFirmId = DA.getFirmId();
    const uri = `/api/task/firm/${viewingFirmId}/taskableGroup/${groupId}`;
    const request = createStdRequest(uri);
    const goodResponseAction = (responseJson) => {
      const groupApiStruct = responseJson;
      // Monitor.recordReceivedStruct('assignableGroup', groupId, groupApiStruct);
      this.store.dispatch(addTaskableGroup(groupApiStruct));
      completionFunc();
    };
    apiFetch(this.page, request, goodResponseAction);
  }

  loadPersonsForQueueOfPersonIds(queue, progressCallback, callbackWhenDone) {
    const workList = queue.map((personId) => {
      return { objType: 'person', id: personId };
    });
    this.processWorkList(workList, callbackWhenDone);
  }

  /*
  loadPersonsForQueueOfPersonIds(queue, progressCallback, callbackWhenDone) {
    if (queue.length === 0) {
      if (callbackWhenDone) {
        callbackWhenDone();
      }
    } else {
      const head = queue[0];
      const tail = queue.slice(1);
      this.loadPersonForPersonIdAndContinue(head, tail, progressCallback, callbackWhenDone);
    }
  }
  */

  loadPersonForPersonIdAndContinue(personId, remainingPersonIds, progressCallback, callbackWhenDone) {
    if (personId === 0 || this.alreadyLoadedPerson(personId)) {
      this.loadPersonsForQueueOfPersonIds(remainingPersonIds, progressCallback, callbackWhenDone);
    } else {
      const uri = '/api/person/' + personId;
      const exposableUri = '[GET] /api/person/<personId>';
      const request = createStdRequest(uri);
      const actionOnGoodResponse = (responseJson) => {
        // store result in Redux
        const personApiStruct = responseJson;
        const reduxPersonStruct = this.createReduxStructForFetchedPersonOrContactApiStruct(personApiStruct);
        this.store.dispatch(addPerson(reduxPersonStruct));
        if (progressCallback) {
          progressCallback();
        }

        // recurse for rest of users
        this.loadPersonsForQueueOfPersonIds(remainingPersonIds, progressCallback, callbackWhenDone);
      };
      apiFetch(this, request, actionOnGoodResponse, exposableUri);
    }
  }

  loadCallRailCallsBetweenDates(page, startDate, endDate, continuation) {
    const firmId = DA.getFirmId();
    const startDateIso = startDate.toISODate();
    const endDateIso = endDate.toISODate();
    const tzOffset = new Date().getTimezoneOffset();
    const uri = `/api/callRail/firm/${firmId}/startDate/${startDateIso}/endDate/${endDateIso}/tzOffset/${tzOffset}/withOutreaches`;
    const request = createStdRequest(uri, 'get');
    const actionOnGoodResponse = (json) => {
      /*
       * stucture of json ("CallsWithOutreaches"):
       * {
       *   outreaches: {
       *     fromClient: [outreach1, outreach2, ...],
       *     fromEmergencyContact: [outreach1, outreach2, ...],
       *     fromReferralContact: [outreach1, outreach2, ...],
       *     fromReferralFirm: [outreach1, outreach2, ...],
       *   },
       *   missives: [missive1, missive2, ...],
       *   calls: [
       *     {
       *       date: <iso8601String>,
       *       calls: [crCall1, crCall2, ...],
       *     },
       *     {
       *       date: <iso8601String>,
       *       calls: [crCall1, crCall2, ...],
       *     },
       *     ...
       *   ],
       * }
       */
      const datedCallsPortion = json.calls;
      const reduxDatedCalls = this.convertGroupedCallRailCallsToReduxFormat(datedCallsPortion);
      this.store.dispatch(addCallRailCallsForDates(reduxDatedCalls));

      let allCalls = [];

      allCalls = datedCallsPortion.reduce((acc, next) => {
        const nextCalls = next.calls;
        return [...acc, ...nextCalls];
      }, []);

      const notesOnCalls = allCalls
        .filter((call) => call.noteId !== 0)
        .map((call) => {
          return { noteId: call.noteId, content: call.noteContent, callRailCallId: call.callRailCallId };
        });
      this.store.dispatch(addOrUpdateNotesOnCallRailCalls(notesOnCalls));

      json.missives.forEach((missive) => this.store.dispatch(addMissive(missive)));

      const outreachPortion = json.outreaches;
      const { fromClient, fromEmergencyContact, fromReferralContact, fromReferralFirm } = outreachPortion;
      const reduxFromClient = this.convertOutreachStructsIntoReduxStructs(fromClient);
      reduxFromClient.forEach((fcOutreach) => {
        const clientId = fcOutreach.senderId;
        this.store.dispatch(addOutreachesFromClient({ clientId, outreaches: [fcOutreach] }));
      });
      const reduxFromEmergencyContact = this.convertOutreachStructsIntoReduxStructs(fromEmergencyContact);
      reduxFromEmergencyContact.forEach((fecOutreach) => {
        const contactId = fecOutreach.senderId;
        this.store.dispatch(addOutreachesFromEmergencyContact({ contactId, outreaches: [fecOutreach] }));
      });
      const reduxFromReferralContact = this.convertOutreachStructsIntoReduxStructs(fromReferralContact);
      reduxFromReferralContact.forEach((frcOutreach) => {
        const contactId = frcOutreach.senderId;
        this.store.dispatch(addOutreachesFromFirmContact({ contactId, outreaches: [frcOutreach] }));
      });
      const reduxFromReferralFirm = this.convertOutreachStructsIntoReduxStructs(fromReferralFirm);
      reduxFromReferralFirm.forEach((frfOutreach) => {
        const firmId = frfOutreach.senderFirmId;
        this.store.dispatch(addOutreachesFromFirm({ firmId, outreaches: [frfOutreach] }));
      });

      const consolidated = this.consolidatedReduxCallRailCallsBetween(startDate, endDate);
      /*
      We'd like to do this kind of map() here, but we could end up with lots of duplicate workItems

      const newWork = consolidated.map(call => {
        return { objType: 'person', id: call.otherPersonId };
      });

      So instead, we do a forEach and avoid duplication
      */
      let newWork = [];
      let idsSeenSoFar = [];
      consolidated.forEach((call) => {
        const otherPersonId = call.otherPersonId;
        if (idsSeenSoFar.indexOf(otherPersonId) < 0) {
          idsSeenSoFar = [...idsSeenSoFar, otherPersonId];
          const newItem = { objType: 'person', id: otherPersonId };
          newWork = [...newWork, newItem];
        }
      });

      this.setPage(page);
      this.processWorkList(newWork, () => continuation(consolidated));
    };
    apiFetch(page, request, actionOnGoodResponse);
  }

  oneDayAsDuration = Duration.fromObject({ days: 1 });

  consolidatedReduxCallRailCallsBetween(startDate, endDate) {
    const callsByDate = this.store.getState().callRailCalls.byDate;
    let accum = [];
    let date = endDate;
    while (date.valueOf() >= startDate.valueOf()) {
      const keyForDate = date.toISO();
      console.log(`keyForDate: ${keyForDate}`);
      console.log(`callsByDate[keyForDate]: ${callsByDate[keyForDate]}`);
      const unresolvedCallsForDate = callsByDate[keyForDate] || [];
      console.log(`unresolvedCallsForDate.length: ${unresolvedCallsForDate.length}`);
      const callsForDate = unresolvedCallsForDate.map((c) => DA.resolveCallRailCallDates(c));
      const sortedCallsForDate = callsForDate.slice(0).sort((c1, c2) => c1.date.valueOf() - c2.date.valueOf());
      console.log(`sortedCallsForDate.length: ${sortedCallsForDate.length}`);
      accum = [...accum, ...sortedCallsForDate];
      console.log(`accum.length: ${accum.length}`);
      date = date.minus(this.oneDayAsDuration);
    }
    return accum;
  }

  convertGroupedCallRailCallsToReduxFormat(apiStructArray) {
    return apiStructArray.reduce((acc, next) => {
      const reduxifiedCalls = this.convertCallRailCallsToReduxFormat(next.calls);
      let date = DateTime.fromISO(next.date).setZone();
      console.log(`date.hour: ${date.hour}`);
      if (date.hour !== 0) {
        console.log(`dateStr: ${date.toISO()}`);
        const minutesInNewZone = date.offset;
        console.log(`offset: ${minutesInNewZone}`);
        const offsetDuration = Duration.fromObject({ minutes: minutesInNewZone });
        const dateMinusOffset = date.minus(offsetDuration);
        console.log(`dateMinusOffset: ${dateMinusOffset.toISO()}`);
        date = dateMinusOffset;
      }
      const dateStr = date.toISO();
      const newEl = { [dateStr]: reduxifiedCalls };
      return { ...acc, ...newEl };
    }, {});
  }

  addCallRailCall(call) {
    const reduxifiedCall = this.convertCallRailCallToReduxFormat(call);
    this.store.dispatch(addCallRailCall(reduxifiedCall));
  }

  convertCallRailCallsToReduxFormat(apiStructArray) {
    return apiStructArray.map((apiStruct) => this.convertCallRailCallToReduxFormat(apiStruct));
  }

  convertCallRailCallToReduxFormat(apiStruct) {
    const truncatedDate = DateTime.fromISO(apiStruct.date).toISO();
    return { ...apiStruct, date: truncatedDate };
  }

  alreadyLoadedThinReferralsFromContact(contactId) {
    const thinReferralsState = this.store.getState().thinReferrals;
    return thinReferralsState.some((r) => r.referrerId === contactId);
  }

  alreadyLoadedPerson(personId) {
    return !!this.store.getState().persons[personId];
  }

  alreadyLoadedContactAtVisibleFirm(personId) {
    const person = this.store.getState().persons[personId];
    return person && person.description !== undefined && person.viewingFirmId !== undefined;
  }

  alreadyLoadedEmergencyContact(personId) {
    const person = this.store.getState().persons[personId];
    return person && person.description !== undefined && person.viewingFirmId !== undefined;
  }

  alreadyLoadedCase(caseId) {
    return this.store.getState().cases.loaded.some((kase) => kase.caseId === caseId);
  }

  alreadyLoadedNotesOnPerson(clientId) {
    return DA.haveNotesOnPersonWithId(clientId);
  }

  alreadyLoadedFirm(firmId) {
    return this.store.getState().firms.some((firm) => firm.firmId === firmId);
  }

  alreadyLoadedFirmAsReferrerOrg(firmId) {
    return this.store.getState().firms.some((firm) => firm.firmId === firmId && firm.facilities);
  }

  alreadyLoadedReferral(referralId) {
    return this.store.getState().referrals.some((referral) => referral.referralId === referralId);
  }

  alreadyLoadedContacts(clientId) {
    return DA.haveContactsForClientWithId(clientId);
  }

  alreadyLoadedTreatmentProgram(programId) {
    return this.store.getState().treatmentPrograms.some((program) => program.treatmentProgramId === programId);
  }

  alreadyLoadedFacility(facilityId) {
    return this.store.getState().facilities.some((facility) => facility.facilityId === facilityId);
  }

  alreadyLoadedOutreachHistoryForPerson(personId) {
    return DA.haveOutreachHistoryForPersonWithId(personId);
  }

  alreadyLoadedStatusHistoryForClient(statusHolderId) {
    const loadedTransitions = this.store.getState().statusTransitions;
    for (let i = 0; i < loadedTransitions.length; i++) {
      if (loadedTransitions[i].subjectId === statusHolderId) {
        return true;
      }
    }
    return false;
  }

  alreadyLoadedDl(dlId) {
    return this.store.getState().distributionLists.some((dl) => dl.distributionListId === dlId);
  }

  alreadyLoadedMissive(missiveId) {
    return this.store.getState().missives.some((missive) => missive.missiveId === missiveId);
  }

  alreadyLoadedCallRailCall(callRailCallId) {
    const allCalls = this.store.getState().callRailCalls.allCalls;
    return !!allCalls.find((call) => call.callRailCallId === callRailCallId);
  }

  alreadyLoadedCampaign(campaignId) {
    return this.store.getState().campaigns.some((campaign) => campaign.campaignId === campaignId);
  }

  alreadyLoadedInsuranceInfo(infoId) {
    return this.store.getState().insuranceInfos.some((info) => info.insuranceInfoId === infoId);
  }

  createReduxStructForFetchedPersonOrContactApiStruct(personApiStruct) {
    const preferredName = personApiStruct.preferredName ? personApiStruct.preferredName : personApiStruct.firstName;
    const preferredFullName = preferredName + ' ' + (personApiStruct.lastName || '');
    const reduxStruct = {
      ...personApiStruct,
      preferredName,
      preferredFullName,
      latestContactDate: personApiStruct.latestContactDate || DateTime.fromMillis(0).toISO(),
    };
    return reduxStruct;
  }

  haveLoadedReferrerInfo() {
    const state = this.store.getState();
    return !!state.orgContacts && state.orgContacts.length > 0;
  }

  processMergeChangeInfo(changeInfo) {
    /* changeInfo looks like this:
        public int VisibleFirmId { get; set; }
        public int TargetContactId { get; set; }
        public int SourceContactId { get; set; }
        public List<int> AddressesToTransfer { get; set; } = new List<int>();
        public List<int> PhoneNumbersToTransfer { get; set; } = new List<int>();
        public List<int> DlMembershipsToClone { get; set; } = new List<int>();
        public List<int> DlMembershipsToDelete { get; set; } = new List<int>();
        public List<int> DocumentsToChange { get; set; } = new List<int>();
        public List<int> OutreachesToChangeReceiver { get; set; } = new List<int>();
        public List<int> OutreachesToChangeSender { get; set; } = new List<int>();
        public List<int> ReferralsToChangeReceiver { get; set; } = new List<int>();
        public List<int> ReferralsToChangeReferrer { get; set; } = new List<int>();
        public List<int> TasksToChangeSubject { get; set; } = new List<int>();
        public bool ClearNotesOnSource { get; set; }
        public List<int> NotesToAddToTarget { get; set; } = new List<int>();
     */
    const {
      visibleFirmId,
      targetContactId,
      sourceContactId,
      addressesToTransfer,
      phoneNumbersToTransfer,
      dlsToAugment,
      dlsToPrune,
      documentsToChange,
      outreachesToChangeReceiver,
      outreachesToChangeSender,
      referralsToChangeReceiver,
      referralsToChangeReferrer,
      tasksToChangeSubject,
      notesToAddToTarget,
    } = changeInfo;
    this.transferAddresses(addressesToTransfer, targetContactId, sourceContactId);
    this.transferPhoneNumbers(phoneNumbersToTransfer, targetContactId, sourceContactId);
    this.augmentDls(dlsToAugment, targetContactId);
    this.pruneDls(dlsToPrune, sourceContactId);
    this.changeDocumentsSubject(documentsToChange, targetContactId);
    this.changeOutreachReceivers(outreachesToChangeReceiver, targetContactId, sourceContactId);
    this.changeOutreachSenders(outreachesToChangeSender, targetContactId, sourceContactId);
    this.changeReferralReceivers(referralsToChangeReceiver, targetContactId);
    this.changeReferralRefeffers(referralsToChangeReferrer, targetContactId);
    this.changeTaskSubjects(tasksToChangeSubject, targetContactId);
    this.addNotesToMergeTarget(notesToAddToTarget, targetContactId, sourceContactId);
    this.removePersonAsFirmContact(visibleFirmId, sourceContactId);
  }

  transferAddresses(addressesToTransfer, targetContactId, sourceContactId) {
    if (addressesToTransfer.length === 0) {
      return;
    }
    addressesToTransfer.forEach((addrId) => this.transferAddress(addrId, targetContactId, sourceContactId));
  }

  transferAddress(addrId, targetContactId, sourceContactId) {
    const target = DA.getPersonById(targetContactId);
    const source = DA.getPersonById(sourceContactId);
    const sourceAddrs = source.addresses;
    const addrToTransfer = sourceAddrs.find((addr) => addr.addressId === addrId);
    const origTargetAddrs = target.addresses;
    const newTargetAddrs = [...origTargetAddrs, addrToTransfer];
    const augmentedTarget = { ...target, addresses: newTargetAddrs };
    this.store.dispatch(updatePerson(augmentedTarget));
  }

  transferPhoneNumbers(phoneNumbersToTransfer, targetContactId, sourceContactId) {
    if (phoneNumbersToTransfer.length === 0) {
      return;
    }
    phoneNumbersToTransfer.forEach((phoneNumberId) =>
      this.transferPhoneNumber(phoneNumberId, targetContactId, sourceContactId)
    );
  }

  transferPhoneNumber(phoneNumberId, targetContactId, sourceContactId) {
    const target = DA.getPersonById(targetContactId);
    const source = DA.getPersonById(sourceContactId);
    const sourcePhoneNumbers = source.phoneNumbers;
    const phoneNumberToTransfer = sourcePhoneNumbers.find((number) => number.phoneId === phoneNumberId);
    const origTargetPhoneNumbers = target.phoneNumbers;
    const newTargetPhoneNumbers = [...origTargetPhoneNumbers, phoneNumberToTransfer];
    const augmentedTarget = { ...target, phoneNumbers: newTargetPhoneNumbers };
    this.store.dispatch(updatePerson(augmentedTarget));
  }

  augmentDls(dlsToAugment, targetContactId) {
    dlsToAugment.forEach((dlId) => this.augmentDl(dlId, targetContactId));
  }

  augmentDl(dlId, targetContactId) {
    const origDl = DA.getDistributionListById(dlId);
    const origMemberIds = origDl.memberIds;
    const augmentedMemberIds = [...origMemberIds, targetContactId];
    const augmentedDl = { ...origDl, memberIds: augmentedMemberIds };
    this.store.dispatch(updateDistributionList(augmentedDl));
  }

  pruneDls(dlsToPrune, sourceContactId) {
    dlsToPrune.forEach((dlId) => this.pruneDl(dlId, sourceContactId));
  }

  pruneDl(dlId, sourceContactId) {
    const origDl = DA.getDistributionListById(dlId);
    const origMemberIds = origDl.memberIds;
    const prunedMemberIds = origMemberIds.filter((id) => id !== sourceContactId);
    const prunedDl = { ...origDl, memberIds: prunedMemberIds };
    this.store.dispatch(updateDistributionList(prunedDl));
  }

  changeDocumentsSubject(documentsToChange, targetContactId) {
    documentsToChange.forEach((docId) => this.changeDocumentSubject(docId, targetContactId));
  }

  changeDocumentSubject(docId, targetContactId) {
    const origDoc = DA.getDocumentById(docId);
    const modifiedDoc = { ...origDoc, subject: targetContactId };
    this.store.dispatch(updateDocument(modifiedDoc));
  }

  changeOutreachReceivers(outreachesToChangeReceiver, targetContactId, sourceContactId) {
    outreachesToChangeReceiver.forEach((oId) => this.changeOutreachReceiver(oId, targetContactId, sourceContactId));
  }

  changeOutreachReceiver(outreachId, targetContactId, sourceContactId) {
    const origOutreach = DA.getOutreachById(outreachId);
    const modifiedOutreach = { ...origOutreach, receiverId: targetContactId };
    this.store.dispatch(updateFirmOutreach(modifiedOutreach));
    this.store.dispatch(
      switchOutreachList({ outreach: modifiedOutreach, oldContactId: sourceContactId, newContactId: targetContactId })
    );
  }

  changeOutreachSenders(outreachesToChangeSender, targetContactId, sourceContactId) {
    outreachesToChangeSender.forEach((oId) => this.changeOutreachSender(oId, targetContactId, sourceContactId));
  }

  changeOutreachSender(outreachId, targetContactId, sourceContactId) {
    const origOutreach = DA.getOutreachById(outreachId);
    const modifiedOutreach = { ...origOutreach, senderId: targetContactId };
    this.store.dispatch(updateFirmOutreach(modifiedOutreach));
    this.store.dispatch(
      switchOutreachList({ outreach: modifiedOutreach, oldContactId: sourceContactId, newContactId: targetContactId })
    );
  }

  changeReferralReceivers(referralsToChangeReceiver, targetContactId) {
    referralsToChangeReceiver.forEach((refId) => this.changeReferralReceiver(refId, targetContactId));
  }

  changeReferralReceiver(refId, targetContactId) {
    const referral = DA.getReferralById(refId);
    const modifiedReferral = { ...referral, receiverId: targetContactId };
    this.store.dispatch(updateReferral(modifiedReferral));
  }

  changeReferralRefeffers(referralsToChangeReferrer, targetContactId) {
    referralsToChangeReferrer.forEach((refId) => this.changeReferralRefeffer(refId, targetContactId));
  }

  changeReferralRefeffer(refId, targetContactId) {
    const referral = DA.getReferralById(refId);
    const modifiedReferral = { ...referral, referrerId: targetContactId };
    this.store.dispatch(updateReferral(modifiedReferral));
  }

  changeTaskSubjects(tasksToChangeSubject, targetContactId) {
    tasksToChangeSubject.forEach((taskId) => this.changeTaskSubject(taskId, targetContactId));
  }

  changeTaskSubject(taskId, targetContactId) {
    const origTask = DA.getTaskById(taskId);
    const modifiedTask = { ...origTask, subjectId: targetContactId };
    this.store.dispatch(replaceTask(modifiedTask));
  }

  addNotesToMergeTarget(notesToAddToTarget, targetContactId, sourceContactId) {
    // const source = DA.getPersonById(sourceContactId);
    // const notesOnSourceContact = DA.getNotesOnPerson(source);
    const notesOnSourceContact = notesToAddToTarget.map((noteId) => DA.getNoteWithId(noteId));
    const changedNotes = notesOnSourceContact.map((note) =>
      this.mergeNoteTargetAndOrSubject(note, targetContactId, sourceContactId)
    );
    this.store.dispatch(addOrUpdateNotesOnPersons(changedNotes));
  }

  mergeNoteTargetAndOrSubject(note, targetContactId, sourceContactId) {
    if (note.targetId === sourceContactId) {
      note = { ...note, targetId: targetContactId };
    }
    if (note.subjectId === sourceContactId) {
      note = { ...note, subjectId: targetContactId };
    }
    return note;
  }

  removePersonAsClient(personId) {
    this.store.dispatch(removeClientFromClientsInfo(personId));
    this.store.dispatch(removePerson(personId));
  }

  removePersonAsEmergencyContact(clientId, contactId) {
    this.store.dispatch(removeContact({ personId: clientId, contactId }));
    // might possibly exist in some other role, so no real need to delete it right now
    // this.store.dispatch(removePerson(contactId));
  }

  removePersonAsFirmContact(visibleFirmId, contactId) {
    this.removeVisibleFirmContact({ visibleFirmId, contactId });
    this.store.dispatch(removePerson(contactId));
  }

  removeVisibleFirmContact(visibleFirmId, contactId) {
    this.store.dispatch(removeVisibleFirmContact({ visibleFirmId, contactId }));
    const origFirm = DA.getFirmById(visibleFirmId);
    const origContactIds = origFirm.contactIds;
    const prunedContactIds = origContactIds.filter((id) => id !== contactId);
    const modifiedFirm = { ...origFirm, contactIds: prunedContactIds };
    this.store.dispatch(updateFirm(modifiedFirm));
  }

  removeUnaffiliatedContact(contactId) {
    this.store.dispatch(removeUnaffiliatedContact(contactId));
  }

  removeNoteOnInquiry(noteId) {
    this.store.dispatch(removeNoteOnInquiry(noteId));
  }

  addDocumentOnInquiry(document) {
    this.store.dispatch(addDocuments([document]));
  }

  addDocuments(docs) {
    this.store.dispatch(addDocuments(docs));
  }

  updateDocumentOnInquiry(document) {
    this.store.dispatch(updateDocument(document));
  }

  updateDocument(document) {
    this.store.dispatch(updateDocument(document));
  }

  removeDocumentOnInquiry(documentId) {
    this.store.dispatch(deleteDocument(documentId));
  }

  loadOrRefreshIncomingOutreach(outreachId, continuation) {
    const firmId = DA.getFirmId();
    const uri = `/api/firm/${firmId}/outreach/${outreachId}`;
    const request = createStdRequest(uri);
    const actionOnGoodResponse = (responseJson) => {
      const outreachApiStruct = responseJson;
      /*
       * At this point we would like to call one of
       *   * addOutreachesFromClient({ clientId, outreaches })
       *   * addOutreachesFromEmergencyContact({ contactId, outreaches })
       *   * addOutreachesFromFirmContact({ contactId, outreaches })
       *   * addOutreachesFromFirm({ firmId, outreaches })
       * and possibly, if it's not for the firm generally
       *   * addOutreaches({ personId, outreaches })
       */
      const senderId = outreachApiStruct.senderId;
      const senderFirmId = outreachApiStruct.senderFirmId;
      const reduxStructArray = this.convertOutreachStructsIntoReduxStructs([outreachApiStruct]);
      if (senderId) {
        if (senderFirmId) {
          // it's a Referral Contact (could it be an emergency contact who happens to be at a firm?)
          logWithTime(
            `Calling addOutreachesFromFirmContact() to add Outreach ${outreachId} to fromOrgContact list in Redux`
          );
          this.store.dispatch(addOutreachesFromFirmContact({ contactId: senderId, outreaches: reduxStructArray }));
        } else {
          // it's either a client or an emergency contact
          if (this.isClient(senderId)) {
            logWithTime(`Calling addOutreachesFromClient() to add Outreach ${outreachId} to fromClient list in Redux`);
            this.store.dispatch(addOutreachesFromClient({ clientId: senderId, outreaches: reduxStructArray }));
          } else {
            logWithTime(
              `Calling addOutreachesFromEmergencyContact() to add Outreach ${outreachId} to fromEmergencyContact list in Redux`
            );
            this.store.dispatch(
              addOutreachesFromEmergencyContact({ contactId: senderId, outreaches: reduxStructArray })
            );
          }
        }
        logWithTime(`Calling addOutreaches() to add Outreach ${outreachId} to person lists of Redux`);
        this.store.dispatch(addOutreaches({ personId: senderId, outreaches: reduxStructArray }));
      } else {
        if (senderFirmId) {
          // it's from a firm but we don't know a person
          logWithTime(`Calling addOutreachesFromFirm() to add Outreach ${outreachId} to fromFirm list in Redux`);
          this.store.dispatch(addOutreachesFromFirm({ firmId: senderFirmId, outreaches: reduxStructArray }));
        } else {
          // This shouldn't happen.  In the case of an anonymous call, no Outreach is created
        }
      }

      continuation();
    };
    logWithTime(`ReduxLoader requests Outreach with ${uri}`);
    apiFetch(null, request, actionOnGoodResponse);
  }

  isClient(personId) {
    const allClientIds = this.store.getState().clientsInfo.clientIds;
    return allClientIds.indexOf(personId) >= 0;
  }

  setUserSetting(settingName, settingStructure) {
    this.store.dispatch(setUserSetting({ settingName, settingStructure }));
  }

  loadOrRefreshMissive(missiveId, continuation) {
    const firmId = DA.getFirmId();
    const uri = `/api/firm/${firmId}/missive/${missiveId}`;
    const request = createStdRequest(uri);
    const actionOnGoodResponse = (responseJson) => {
      const missiveApiStruct = responseJson;
      const reduxStruct = this.convertMissiveApiStructIntoReduxStruct(missiveApiStruct);
      logWithTime(`Calling addMissive() to add Missive ${missiveId} to Redux`);
      this.store.dispatch(addMissive(reduxStruct));
      continuation();
    };
    logWithTime(`ReduxLoader requests Missive with ${uri}`);
    apiFetch(null, request, actionOnGoodResponse);
  }

  loadOrRefreshCallRailCall(callId, continuation) {
    const uri = `/api/callrail/${callId}`;
    const request = createStdRequest(uri);
    const actionOnGoodResponse = (responseJson) => {
      const callRailCallApiStruct = responseJson;
      logWithTime(`Calling addCallRailCall() to add CallRailCall ${callId} to Redux`);
      this.addCallRailCall(callRailCallApiStruct);
      continuation();
    };
    logWithTime(`ReduxLoader requests CallRailCall with ${uri}`);
    apiFetch(null, request, actionOnGoodResponse);
  }

  deleteCallRailCall(callId) {
    this.store.dispatch(deleteCallRailCall(callId));
  }

  deleteCaseWithId(caseId) {
    this.store.dispatch(deleteCase(caseId));
  }

  deleteMissiveWithId(missiveId) {
    this.store.dispatch(deleteMissiveWithId(missiveId));
  }

  /*
  deleteOutreachWithId(outreachId) {
    this.store.dispatch(deleteOutreachWithId(outreachId));
    this.store.dispatch(deleteOutreachWithIdFromLists(outreachId));
  }
  */

  deleteReferralWithId(referralId) {
    this.store.dispatch(deleteReferralWithId(referralId));
    this.store.dispatch(deleteThinReferralWithId(referralId));
  }

  loadTask(taskId, continuation) {
    const uri = `/api/task/${taskId}`;
    const request = createStdRequest(uri);
    const actionOnGoodResponse = (task) => {
      this.store.dispatch(addTask(task));
      continuation();
    };
    apiFetch(null, request, actionOnGoodResponse);
  }

  refreshTask(taskId, continuation) {
    const uri = `/api/task/${taskId}`;
    const request = createStdRequest(uri);
    const actionOnGoodResponse = (task) => {
      this.store.dispatch(replaceTask(task));
      continuation();
    };
    apiFetch(null, request, actionOnGoodResponse);
  }

  deleteTaskWithId(taskId) {
    this.store.dispatch(deleteTaskWithId(taskId));
  }

  changeInquiryOwnerships(oldOwnerId, newOwnerId) {
    const payload = { oldOwnerId, newOwnerId };
    this.store.dispatch(bulkChangeInquiryOwnerships(payload));
  }

  changeClientOwnerships(oldOwnerId, newOwnerId) {
    const payload = { oldOwnerId, newOwnerId };
    this.store.dispatch(bulkChangeClientOwnerships(payload));
  }

  changeReferralContactOwnerships(oldOwnerId, newOwnerId) {
    const payload = { oldOwnerId, newOwnerId };
    this.store.dispatch(bulkChangeOrgContactOwnerships(payload));
  }

  refreshReferral(referralId, continuation) {
    const uri = `/api/referral/${referralId}`;
    const request = createStdRequest(uri);
    const actionOnGoodResponse = (referral) => {
      this.store.dispatch(updateReferral(referral));
      continuation();
    };
    apiFetch(null, request, actionOnGoodResponse);
  }
}

export default new ReduxLoader();
